import { formatDate, formatDateTime } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import React, { useState, useEffect, useCallback, useReducer, useRef, useId, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, RotateCcw, CircleHelp } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DraggableProvidedDragHandleProps, type DropResult } from 'react-beautiful-dnd';
import { clampStepDragStyle, notificationOverrideState, shouldCloseStepBackdrop } from './workflowStepBuilderPolicy';
import './WorkflowsManagement.css';
import { WorkflowNotificationContent, WorkflowNotificationGuideContent } from './WorkflowNotificationContent';
import { notificationChannelTargetLabel } from './notificationChannelTargetLabel';
import type { ContentSource } from './workflowNotificationContentHelpers';
import { schemaContentPaths } from './workflowNotificationContentHelpers';
import { WorkflowVariableBrowser } from './WorkflowVariableBrowser';
import { ResourceCloneButton } from './ResourceCloneModal';
import { WorkflowInboundIntegrationSelect } from './WorkflowInboundIntegrationSelect';
import {
  tenantApi,
  type WorkflowDto,
  type WorkflowInstanceDto,
  type WorkflowStateEntryDto,
  type WorkflowTriggerConditions,
  type WorkflowStepRequest,
  type WorkflowStepDto,
  type WorkflowConditionMetadata,
  type WorkflowSecret,
  type NotificationChannel,
  type ExchangeSenderRegistration,
  type User,
  type Group,
  type Integration,
  type PromoteWorkflowWebhookStepResponse,
  type RebindWorkflowIntegrationStepResponse,
  type OutboundIntegration,
  type OutboundIntegrationAction,
  type EscalationPolicyDto,
  type Schedule,
  type InboundIntegration,
  type CreateWorkflowRequest,
  type UpdateWorkflowRequest,
  type SubWorkflowDto,
  type CreateSubWorkflowRequest,
  type UpdateSubWorkflowRequest,
  type SubWorkflowUsageResponse,
} from '../../services/tenantApi';
import {
  WORKFLOW_TRIGGER_TYPES,
  getWorkflowAssociationChange,
  getWorkflowTriggerChoices,
  isNotificationWorkflow,
  isNotificationSafeStep,
  isNotificationSafeAction,
  validateWorkflowSource,
} from './workflowSourcePolicy';

type WorkflowDialogRequest = { title: string; message: string; action: string; defaultValue?: string; destructive?: boolean };

function useWorkflowDialog() {
  const [request, setRequest] = useState<WorkflowDialogRequest | null>(null);
  const [value, setValue] = useState('');
  const resolver = useRef<((result: string | null) => void) | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const messageId = useId();
  useEffect(() => () => { resolver.current?.(null); }, []);
  useEffect(() => {
    if (!request) return;
    const previous = document.activeElement;
    panel.current?.querySelector<HTMLElement>('input, button')?.focus();
    return () => { if (previous instanceof HTMLElement && previous.isConnected) previous.focus(); };
  }, [request]);
  const finish = (result: string | null) => {
    resolver.current?.(result);
    resolver.current = null;
    setRequest(null);
  };
  const ask = (next: WorkflowDialogRequest) => new Promise<string | null>(resolve => {
    resolver.current?.(null);
    resolver.current = resolve;
    setValue(next.defaultValue ?? '');
    setRequest(next);
  });
  const dialog = request && createPortal(
    <div className="modal-overlay workflow-unsaved-confirmation-overlay" onClick={event => {
      event.stopPropagation();
      if (event.target === event.currentTarget) finish(null);
    }} onKeyDown={event => {
      event.stopPropagation();
      if (event.key === 'Escape') { event.preventDefault(); finish(null); }
      if (event.key === 'Tab') {
        const controls = panel.current?.querySelectorAll<HTMLElement>('input, button:not(:disabled)');
        if (!controls?.length) return;
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    }}>
      <div ref={panel} className="modal modal-sm workflow-unsaved-confirmation" role="alertdialog" aria-modal="true"
        aria-labelledby={titleId} aria-describedby={messageId}>
        <form onSubmit={event => { event.preventDefault(); if (request.defaultValue === undefined || value.trim()) finish(value.trim()); }}>
          <h3 id={titleId}>{request.title}</h3>
          <p id={messageId}>{request.message}</p>
          {request.defaultValue !== undefined && <label>Shared sub-workflow name
            <input aria-label="Shared sub-workflow name" value={value} required onChange={event => setValue(event.target.value)} />
          </label>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => finish(null)}>Cancel</button>
            <button type="submit" className={request.destructive ? 'btn-danger' : 'btn-primary'}
              disabled={request.defaultValue !== undefined && !value.trim()}>{request.action}</button>
          </div>
        </form>
      </div>
    </div>, document.body);
  return { ask, dialog };
}

// ─── Constants ──────────────────────────────────────────────────────────────

const TRIGGER_TYPES = WORKFLOW_TRIGGER_TYPES;
const SEVERITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];
const STEP_TYPES = [
  { value: 'Notification', label: 'Send Notification' },
  { value: 'Delay', label: 'Wait / Delay' },
  { value: 'Ping', label: 'Ping' },
  { value: 'Webhook', label: 'Webhook/API' },
  { value: 'IntegrationAction', label: 'Integration Action' },
  { value: 'DispatchOutboundDelivery', label: 'Dispatch Outbound Delivery' },
  { value: 'UpdateAlert', label: 'Update Alert' },
  { value: 'Escalate', label: 'Escalate' },
  { value: 'Condition', label: 'Condition' },
  { value: 'RunSubWorkflow', label: 'Run Sub-Workflow' },
  { value: 'ReturnResult', label: 'Return Result' },
  { value: 'EndWorkflow', label: 'End Workflow' },
];

const NOTIFICATION_TARGET_TYPES = ['Channel', 'User', 'Group', 'OnCall'] as const;
const NOTIFICATION_CHANNEL_TYPES = ['Email', 'Teams', 'TeamsWorkflow', 'Webhook', 'SMS'] as const;

const DEFAULT_CONDITION_FIELD_OPTIONS: Array<{ value: string; label: string; category: string }> = [
  { value: 'alert.id', label: 'Alert ID', category: 'Core' },
  { value: 'alert.title', label: 'Title', category: 'Core' },
  { value: 'alert.description', label: 'Description', category: 'Core' },
  { value: 'alert.severity', label: 'Severity', category: 'Core' },
  { value: 'alert.status', label: 'Status', category: 'Core' },
  { value: 'alert.service', label: 'Service', category: 'Core' },
  { value: 'alert.source', label: 'Source', category: 'Core' },
  { value: 'alert.assignee', label: 'Assignee', category: 'Core' },
  { value: 'alert.resolution', label: 'Resolution', category: 'Core' },
  { value: 'alert.correlationid', label: 'Correlation ID', category: 'Identifiers' },
  { value: 'alert.externalalertid', label: 'External Alert ID', category: 'Identifiers' },
  { value: 'alert.externalticketid', label: 'External Ticket ID', category: 'Identifiers' },
  { value: 'alert.createdat', label: 'Created At', category: 'Time' },
  { value: 'alert.occurredat', label: 'Occurred At', category: 'Time' },
  { value: 'alert.tenantid', label: 'Tenant ID', category: 'Tenant' },
];

const DEFAULT_CONDITION_OPERATORS = [
  { value: 'equals', label: 'Equals', hint: 'Exact case-insensitive match.' },
  { value: 'notequals', label: 'Not equals', hint: 'True when values do not match.' },
  { value: 'contains', label: 'Contains', hint: 'Substring match for text values.' },
  { value: 'startswith', label: 'Starts with', hint: 'Prefix match for text values.' },
  { value: 'endswith', label: 'Ends with', hint: 'Suffix match for text values.' },
  { value: 'in', label: 'In (comma-separated)', hint: 'Match any value in a comma-separated list.' },
  { value: 'isempty', label: 'Is empty', hint: 'True when the field is blank or missing. Value is ignored.' },
  { value: 'isnotempty', label: 'Is not empty', hint: 'True when the field has a value. Value is ignored.' },
];

const CURRENT_ALERT_CONDITION_FIELDS = [
  { value: 'alert.status', label: 'Status' },
  { value: 'alert.externalticketid', label: 'External ticket ID' },
];
const VALUELESS_CONDITION_OPERATORS = new Set(['isempty', 'is_empty', 'isnotempty', 'is_not_empty']);

const CONDITION_CUSTOM_FIELD_VALUE = '__custom__';
const INTEGRATION_VERSION_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
const INTEGRATION_FIELD_TOKEN_PATTERN = /\{\{\s*integration\.fields\.([A-Za-z0-9._-]+)\s*\}\}/g;
// Backend endpoints for private sub-workflows remain supported, but UI is intentionally hidden for now.
const PRIVATE_SUBWORKFLOW_UI_ENABLED = false;

function extractIntegrationFieldTokens(...templates: Array<string | undefined>): string[] {
  const found = new Set<string>();

  templates
    .filter((t): t is string => typeof t === 'string' && t.length > 0)
    .forEach((template) => {
      const matches = template.matchAll(INTEGRATION_FIELD_TOKEN_PATTERN);
      for (const match of matches) {
        const key = (match[1] || '').trim();
        if (key) {
          found.add(key);
        }
      }
    });

  return Array.from(found).sort((a, b) => a.localeCompare(b));
}

function extractOutboundBodySchemaPaths(template?: string): string[] {
  if (!template) return [];
  try {
    const body = JSON.parse(template);
    const paths = new Set<string>();
    const visit = (value: unknown, path: string) => {
      if (Array.isArray(value)) {
        if (value.length) visit(value[0], `${path}/0`);
        return;
      }
      if (value && typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>);
        if (!entries.length && path) paths.add(path);
        for (const [key, child] of entries) {
          const segment = key.replace(/~/g, '~0').replace(/\//g, '~1');
          visit(child, `${path}/${segment}`);
        }
        return;
      }
      if (path) paths.add(path);
    };
    visit(body, '');
    return Array.from(paths).sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

function getOutboundActionSchemaPaths(action?: OutboundIntegrationAction): string[] {
  if (!action) return [];
  if (action.requestSchemaJson?.trim()) {
    try { return schemaContentPaths(action.requestSchemaJson); }
    catch { return []; }
  }
  return extractOutboundBodySchemaPaths(action.bodyTemplate);
}

function inferWorkflowVariableForFieldKey(fieldKey: string): string {
  const segments = fieldKey.split('/').filter(Boolean);
  const normalized = (segments[segments.length - 1] || fieldKey).replace(/~1/g, '/').replace(/~0/g, '~').toLowerCase();
  if (normalized === 'title' || normalized.endsWith('.title')) return 'alert.title';
  if (normalized === 'description' || normalized === 'details' || normalized.endsWith('.description')) return 'alert.description';
  if (normalized === 'severity' || normalized.endsWith('.severity')) return 'alert.severity';
  if (normalized === 'status' || normalized.endsWith('.status')) return 'alert.status';
  if (normalized === 'service' || normalized.endsWith('.service')) return 'alert.service';
  if (normalized === 'source' || normalized.endsWith('.source')) return 'alert.source';
  if (normalized === 'ticketid' || normalized.endsWith('.ticketid')) return 'alert.externalticketid';
  if (normalized === 'correlationid' || normalized.endsWith('.correlationid')) return 'alert.correlationid';
  return `alert.data.${fieldKey}`;
}

// ─── Main Component ─────────────────────────────────────────────────────────

export const WorkflowsManagement: React.FC<{ canClone?: boolean }> = ({ canClone = false }) => {
  const { ask, dialog } = useWorkflowDialog();
  useDateTimePreferences();
  const [workflows, setWorkflows] = useState<WorkflowDto[]>([]);
  const [subWorkflows, setSubWorkflows] = useState<SubWorkflowDto[]>([]);
  const [inboundIntegrations, setInboundIntegrations] = useState<InboundIntegration[]>([]);
  const [inboundError, setInboundError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showGuide, setShowGuide] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [activeTab, setActiveTab] = useState<'workflows' | 'subworkflows'>('workflows');
  const [subWorkflowSearch, setSubWorkflowSearch] = useState('');
  const [subWorkflowStatus, setSubWorkflowStatus] = useState('all');
  const [subWorkflowModal, setSubWorkflowModal] = useState<SubWorkflowDto | 'create' | null>(null);
  const [subWorkflowError, setSubWorkflowError] = useState<string | null>(null);
  const [subWorkflowSubmitting, setSubWorkflowSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WorkflowDto | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [modal, setModal] = useState<
    | { type: 'create' }
    | { type: 'edit'; workflow: WorkflowDto }
    | { type: 'steps'; workflow: WorkflowDto }
    | { type: 'subworkflow-steps'; workflow: SubWorkflowDto }
    | { type: 'history'; workflow: WorkflowDto }
    | null
  >(null);

  const loadWorkflows = useCallback(async (refreshOnly = false) => {
    if (!refreshOnly) setLoading(true);
    setInboundError(null);
    setSubWorkflowError(null);
    try {
      const [data, inboundData, subWorkflowData] = await Promise.all([
        tenantApi.getWorkflows(),
        tenantApi.getInboundIntegrations().catch(() => {
          setInboundError('Inbound integrations could not be loaded. Association safety cannot be verified.');
          return [];
        }),
        tenantApi.getSharedSubWorkflows().catch(() => {
          setSubWorkflowError('Sub-workflows could not be loaded. Shared sub-workflow actions are temporarily unavailable.');
          return [];
        }),
      ]);
      setWorkflows(data);
      setSubWorkflows(subWorkflowData);
      setInboundIntegrations(inboundData);
    } catch (err) {
      console.error('Failed to load workflows:', err);
      if (refreshOnly) throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWorkflows(); }, [loadWorkflows]);

  const handleCreate = async (data: CreateWorkflowRequest) => {
    const newWorkflow = await tenantApi.createWorkflow(data);
    setModal({ type: 'steps', workflow: newWorkflow }); // Open step builder right away
    await loadWorkflows();
  };

  const handleUpdate = async (workflowId: string, data: UpdateWorkflowRequest) => {
    const updatedWorkflow = await tenantApi.updateWorkflow(workflowId, data);
    setModal(null);
    await loadWorkflows();
    setWorkflows(current => current
      .map(workflow => workflow.workflowId === workflowId ? updatedWorkflow : workflow)
      .sort((left, right) => left.name.localeCompare(right.name)));
  };

  const handleDelete = (workflow: WorkflowDto) => {
    setDeleteError(null);
    setDeleteTarget(workflow);
  };

  const confirmDeleteWorkflow = async () => {
    if (!deleteTarget || deleteSubmitting) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      await tenantApi.deleteWorkflow(deleteTarget.workflowId);
      setDeleteTarget(null);
      await loadWorkflows();
    } catch (err: any) {
      setDeleteError(err?.message || 'Failed to delete workflow');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleToggle = async (workflowId: string) => {
    await tenantApi.toggleWorkflow(workflowId);
    await loadWorkflows();
  };

  const saveSubWorkflow = async (data: CreateSubWorkflowRequest | UpdateSubWorkflowRequest) => {
    setSubWorkflowSubmitting(true);
    setSubWorkflowError(null);
    try {
      if (subWorkflowModal && subWorkflowModal !== 'create') {
        await tenantApi.updateSharedSubWorkflow(subWorkflowModal.workflowId, {
          ...data,
          expectedDefinitionRevision: subWorkflowModal.definitionRevision,
        } as UpdateSubWorkflowRequest);
      } else {
        await tenantApi.createSharedSubWorkflow(data as CreateSubWorkflowRequest);
      }
      setSubWorkflowModal(null);
      await loadWorkflows();
    } catch (err) {
      setSubWorkflowError(err instanceof Error ? err.message : 'Sub-workflow could not be saved.');
    } finally {
      setSubWorkflowSubmitting(false);
    }
  };

  const toggleSubWorkflow = async (subWorkflow: SubWorkflowDto) => {
    setSubWorkflowError(null);
    try {
      if (subWorkflow.isEnabled) {
        const usage = await tenantApi.getSharedSubWorkflowUsage(subWorkflow.workflowId);
        if (usage.callers.length || usage.runningInstances.length) {
          setSubWorkflowError(formatSubWorkflowBlockers('disable', usage));
          return;
        }
      }
      await tenantApi.updateSharedSubWorkflow(subWorkflow.workflowId, {
        expectedDefinitionRevision: subWorkflow.definitionRevision,
        isEnabled: !subWorkflow.isEnabled,
      });
      await loadWorkflows();
    } catch (err) {
      setSubWorkflowError(err instanceof Error ? err.message : 'Sub-workflow status could not be changed.');
    }
  };

  const deleteSubWorkflow = async (subWorkflow: SubWorkflowDto) => {
    setSubWorkflowError(null);
    try {
      const usage = await tenantApi.getSharedSubWorkflowUsage(subWorkflow.workflowId);
      if (usage.callers.length || usage.runningInstances.length) {
        setSubWorkflowError(formatSubWorkflowBlockers('delete', usage));
        return;
      }
      if (await ask({ title: 'Delete shared sub-workflow?', message: `Delete shared sub-workflow "${subWorkflow.name}"? This cannot be undone.`, action: 'Delete sub-workflow', destructive: true }) === null) return;
      await tenantApi.deleteSharedSubWorkflow(subWorkflow.workflowId);
      await loadWorkflows();
    } catch (err) {
      setSubWorkflowError(err instanceof Error ? err.message : 'Sub-workflow could not be deleted.');
    }
  };

  const getTriggerLabel = (type: string) => TRIGGER_TYPES.find(t => t.value === type)?.label || type;
  const query = search.trim().toLowerCase();
  const filteredWorkflows = workflows.filter(workflow => {
    if (statusFilter !== 'all' && workflow.isEnabled !== (statusFilter === 'enabled')) return false;
    if (triggerFilter !== 'all' && workflow.triggerType !== triggerFilter) return false;
    const integration = inboundIntegrations.find(item => item.inboundIntegrationId === workflow.inboundIntegrationId);
    return !query || [workflow.name, workflow.description, getTriggerLabel(workflow.triggerType),
      integration?.name, workflow.inboundIntegrationId ? integration?.nonAlert ? 'Notification' : 'Alert' : 'Global / legacy',
      ...(workflow.triggerConditions?.severity ?? []), ...(workflow.triggerConditions?.service ?? []),
      ...(workflow.triggerConditions?.source ?? [])].some(value => value?.toLowerCase().includes(query));
  });
  const subQuery = subWorkflowSearch.trim().toLowerCase();
  const filteredSubWorkflows = subWorkflows.filter(workflow =>
    (subWorkflowStatus === 'all' || workflow.isEnabled === (subWorkflowStatus === 'enabled')) &&
    (!subQuery || [workflow.name, workflow.description, workflow.sourceCompatibility,
      ...workflow.resultCodes].some(value => value?.toLowerCase().includes(subQuery))));

  return (
    <div className="management-section">
      {dialog}
      <div className="section-header">
        <h2>Workflows</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={() => setShowGuide(!showGuide)}>
            {showGuide ? 'Hide Guide' : 'How It Works'}
          </button>
          <button className="btn-primary" onClick={() => {
            if (activeTab === 'workflows') {
              setModal({ type: 'create' });
              return;
            }
            setSubWorkflowError(null);
            setSubWorkflowModal('create');
          }}>
            {activeTab === 'workflows' ? '+ Create Workflow' : '+ New Sub-Workflow'}
          </button>
        </div>
      </div>

      <div className="workflow-tabs" role="tablist" aria-label="Workflow definitions">
        <button type="button" role="tab" aria-selected={activeTab === 'workflows'}
          onClick={() => setActiveTab('workflows')}>Workflows</button>
        <button type="button" role="tab" aria-selected={activeTab === 'subworkflows'}
          onClick={() => setActiveTab('subworkflows')}>Sub-workflows</button>
      </div>

      {showGuide && (activeTab === 'workflows' ? <WorkflowGuide /> : <SubWorkflowGuide />)}

      {activeTab === 'workflows' && <>
      {inboundError && <div className="error-message" role="alert">{inboundError}</div>}

      <div className="workflow-list-filters" role="search" aria-label="Workflow filters">
        <label>Search<input type="search" aria-label="Search workflows" value={search} onChange={event => setSearch(event.target.value)} /></label>
        <label>Status<select aria-label="Workflow status" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
          <option value="all">All statuses</option><option value="enabled">Enabled</option><option value="disabled">Disabled</option>
        </select></label>
        <label>Trigger<select aria-label="Workflow trigger" value={triggerFilter} onChange={event => setTriggerFilter(event.target.value)}>
          <option value="all">All triggers</option>{TRIGGER_TYPES.map(trigger => <option key={trigger.value} value={trigger.value}>{trigger.label}</option>)}
        </select></label>
        <button type="button" className="btn-icon" aria-label="Reset workflow filters" title="Reset workflow filters"
          disabled={!search && statusFilter === 'all' && triggerFilter === 'all'}
          onClick={() => { setSearch(''); setStatusFilter('all'); setTriggerFilter('all'); }}><RotateCcw size={16} /></button>
      </div>

      {loading ? (
        <div className="loading">Loading workflows...</div>
      ) : (
        <>
        <p role="status">{filteredWorkflows.length} of {workflows.length} workflows</p>
        <div className="workflow-list-table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Trigger</th>
              <th>Conditions</th>
              <th className="status-column">Status</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkflows.map((wf) => (
              <tr key={wf.workflowId}>
                <td>
                  <strong>{wf.name}</strong>
                  {wf.description && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #888)', marginTop: 2 }}>
                      {wf.description}
                    </div>
                  )}
                </td>
                <td>
                  {getTriggerLabel(wf.triggerType)}
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary, #888)' }}>
                    {wf.inboundIntegrationId
                      ? (() => {
                          const integration = inboundIntegrations.find(item => item.inboundIntegrationId === wf.inboundIntegrationId);
                          return integration ? `${integration.name} (${integration.nonAlert ? 'Notification' : 'Alert'})` : `Unavailable integration (${wf.inboundIntegrationId})`;
                        })()
                      : 'Global / legacy'}
                  </div>
                </td>
                <td><TriggerConditionsSummary conditions={wf.triggerConditions} /></td>
                <td className="status-column">
                  <span className={`status-badge ${wf.isEnabled ? 'enabled' : 'disabled'}`}>
                    {wf.isEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td>{formatDate(wf.updatedAt)}</td>
                <td>
                  <div className="workflow-actions-row">
                    <button className="btn-icon" aria-label={`Edit steps for ${wf.name}`} title="Edit steps" onClick={() => setModal({ type: 'steps', workflow: wf })}>&#x2699;&#xFE0F;</button>
                    <button className="btn-icon" aria-label={`History for ${wf.name}`} title="History" onClick={() => setModal({ type: 'history', workflow: wf })}>&#x1F4CB;</button>
                    <button className="btn-icon" aria-label={`Edit ${wf.name}`} title="Edit" onClick={() => setModal({ type: 'edit', workflow: wf })}>&#x270F;&#xFE0F;</button>
                    {canClone && <ResourceCloneButton source={{ rootKind: 'workflow', rootId: wf.workflowId, name: wf.name, inboundIntegrationId: wf.inboundIntegrationId }}
                      onCreated={() => loadWorkflows(true)}
                      onOpen={async result => setModal({ type: 'steps', workflow: await tenantApi.getWorkflow(result.rootId) })} />}
                    <button className="btn-icon" aria-label={`${wf.isEnabled ? 'Disable' : 'Enable'} ${wf.name}`} title={wf.isEnabled ? 'Disable' : 'Enable'} onClick={() => handleToggle(wf.workflowId)}>
                      {wf.isEnabled ? '\u23F8' : '\u25B6'}
                    </button>
                    <button className="btn-icon" title="Delete" aria-label={`Delete ${wf.name}`} onClick={() => handleDelete(wf)}>&#x1F5D1;&#xFE0F;</button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredWorkflows.length === 0 && (
              <tr><td colSpan={6} className="empty-row">{workflows.length ? 'No workflows match these filters.' : 'No workflows configured.'}</td></tr>
            )}
          </tbody>
        </table>
        </div>
        </>
      )}
      </>}

      {activeTab === 'subworkflows' && <>
        <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Sub-workflows</h3>
        {subWorkflowError && <div className="error-message" role="alert">{subWorkflowError}</div>}
        <div className="workflow-list-filters subworkflow-filters" role="search" aria-label="Sub-workflow filters">
          <label>Search<input type="search" aria-label="Search sub-workflows" value={subWorkflowSearch}
            onChange={event => setSubWorkflowSearch(event.target.value)} /></label>
          <label>Status<select aria-label="Sub-workflow status" value={subWorkflowStatus}
            onChange={event => setSubWorkflowStatus(event.target.value)}>
            <option value="all">All statuses</option><option value="enabled">Enabled</option><option value="disabled">Disabled</option>
          </select></label>
          <button type="button" className="btn-icon" aria-label="Reset sub-workflow filters" title="Reset sub-workflow filters"
            disabled={!subWorkflowSearch && subWorkflowStatus === 'all'}
            onClick={() => { setSubWorkflowSearch(''); setSubWorkflowStatus('all'); }}><RotateCcw size={16} /></button>
        </div>
        <p role="status">{filteredSubWorkflows.length} of {subWorkflows.length} sub-workflows</p>
        <div className="workflow-list-table-scroll">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Source compatibility</th><th>Result codes</th><th>Usage</th><th className="status-column">Status</th><th>Updated</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredSubWorkflows.map(workflow => <SubWorkflowRow key={workflow.workflowId} workflow={workflow}
                canClone={canClone}
                onCloned={() => loadWorkflows(true)}
                onOpenClone={async id => setModal({ type: 'subworkflow-steps', workflow: await tenantApi.getSharedSubWorkflow(id) })}
                onEdit={() => { setSubWorkflowError(null); setSubWorkflowModal(workflow); }}
                onEditSteps={() => setModal({ type: 'subworkflow-steps', workflow })}
                onToggle={() => toggleSubWorkflow(workflow)} onDelete={() => deleteSubWorkflow(workflow)} />)}
              {!filteredSubWorkflows.length && <tr><td colSpan={7} className="empty-row">{subWorkflows.length ? 'No sub-workflows match these filters.' : 'No shared sub-workflows configured.'}</td></tr>}
            </tbody>
          </table>
        </div>
      </>}

      {modal?.type === 'create' && <WorkflowModal inboundIntegrations={inboundIntegrations} inboundError={inboundError} onClose={() => setModal(null)} onSave={handleCreate} />}
      {modal?.type === 'edit' && <WorkflowModal workflow={modal.workflow} inboundIntegrations={inboundIntegrations} inboundError={inboundError} onClose={() => setModal(null)} onSave={(data) => handleUpdate(modal.workflow.workflowId, data)} />}
      {modal?.type === 'steps' && <StepsBuilderModal workflow={modal.workflow} inboundIntegrations={inboundIntegrations} inboundError={inboundError} onClose={() => { setModal(null); loadWorkflows(); }} />}
      {modal?.type === 'subworkflow-steps' && <StepsBuilderModal workflow={toWorkflowDto(modal.workflow)}
        subWorkflowSource={modal.workflow.sourceCompatibility} expectedDefinitionRevision={modal.workflow.definitionRevision}
        declaredResultCodes={modal.workflow.resultCodes}
        subWorkflowScope="Shared"
        inboundIntegrations={inboundIntegrations} inboundError={inboundError}
        onClose={() => { setModal(null); loadWorkflows(); }} />}
      {modal?.type === 'history' && <InstanceHistoryModal workflow={modal.workflow} onClose={() => setModal(null)} />}
      {subWorkflowModal && <SharedSubWorkflowModal workflow={subWorkflowModal === 'create' ? undefined : subWorkflowModal}
        submitting={subWorkflowSubmitting} error={subWorkflowError} onClose={() => { setSubWorkflowModal(null); setSubWorkflowError(null); }}
        onSave={saveSubWorkflow} />}
      {deleteTarget && <div className="modal-overlay" onMouseDown={event => {
        if (event.target === event.currentTarget && !deleteSubmitting) setDeleteTarget(null);
      }}>
        <div className="modal modal-sm" role="alertdialog" aria-modal="true" aria-labelledby="delete-workflow-title"
          onClick={event => event.stopPropagation()}>
          <h3 id="delete-workflow-title">Delete workflow</h3>
          <p>Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</p>
          {deleteError && <div className="error-message" role="alert">{deleteError}</div>}
          <div className="form-actions">
            <button type="button" className="btn-secondary" disabled={deleteSubmitting}
              onClick={() => { setDeleteTarget(null); setDeleteError(null); }}>Cancel</button>
            <button type="button" className="btn-danger" disabled={deleteSubmitting} onClick={() => void confirmDeleteWorkflow()}>
              {deleteSubmitting ? 'Deleting...' : 'Delete workflow'}
            </button>
          </div>
        </div>
      </div>}
    </div>
  );
};

const formatSubWorkflowBlockers = (action: 'disable' | 'delete', usage: SubWorkflowUsageResponse) => {
  const callers = usage.callers.map(caller => caller.name || caller.workflowId);
  const running = usage.runningInstances.map(instance => instance.instanceId);
  const details = [
    callers.length ? `callers: ${callers.join(', ')}` : '',
    running.length ? `running instances: ${running.join(', ')}` : '',
  ].filter(Boolean).join('; ');
  return `Cannot ${action} this sub-workflow while it is in use${details ? ` (${details})` : ''}.`;
};

const toWorkflowDto = (workflow: SubWorkflowDto): WorkflowDto => ({
  workflowId: workflow.workflowId,
  inboundIntegrationId: null,
  globalVariables: workflow.globalVariables ?? [],
  name: workflow.name,
  description: workflow.description,
  triggerType: 'Manual',
  triggerConditions: null,
  isEnabled: workflow.isEnabled,
  createdBy: null,
  createdAt: workflow.createdAt,
  updatedAt: workflow.updatedAt,
});

const SubWorkflowRow: React.FC<{
  workflow: SubWorkflowDto;
  onEdit: () => void;
  onEditSteps: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onCloned: () => Promise<void>;
  onOpenClone: (id: string) => Promise<void>;
  canClone: boolean;
}> = ({ workflow, onEdit, onEditSteps, onToggle, onDelete, onCloned, onOpenClone, canClone }) => {
  const [usageCount, setUsageCount] = useState<number | null>(null);
  useEffect(() => {
    let active = true;
    tenantApi.getSharedSubWorkflowUsage(workflow.workflowId)
      .then(usage => { if (active) setUsageCount(usage.callers.length); })
      .catch(() => { if (active) setUsageCount(null); });
    return () => { active = false; };
  }, [workflow.workflowId]);

  return <tr>
    <td><strong>{workflow.name}</strong>{workflow.description && <div className="subworkflow-description">{workflow.description}</div>}</td>
    <td>{workflow.sourceCompatibility}</td>
    <td>{workflow.resultCodes.length ? workflow.resultCodes.join(', ') : <span className="subworkflow-muted">None declared</span>}</td>
    <td>{usageCount == null ? <span className="subworkflow-muted">Unavailable</span> : `${usageCount} ${usageCount === 1 ? 'workflow' : 'workflows'}`}</td>
    <td className="status-column"><span className={`status-badge ${workflow.isEnabled ? 'enabled' : 'disabled'}`}>{workflow.isEnabled ? 'Enabled' : 'Disabled'}</span></td>
    <td>{formatDate(workflow.updatedAt)}</td>
    <td><div className="workflow-actions-row">
      <button type="button" className="btn-icon" aria-label={`Edit steps for ${workflow.name}`} title="Edit steps" onClick={onEditSteps}>&#x2699;&#xFE0F;</button>
      <button type="button" className="btn-icon" aria-label={`Edit ${workflow.name}`} title="Edit" onClick={onEdit}>&#x270F;&#xFE0F;</button>
      {canClone && <ResourceCloneButton source={{ rootKind: 'workflow', rootId: workflow.workflowId, name: workflow.name, isSubWorkflow: true }}
        onCreated={onCloned} onOpen={result => onOpenClone(result.rootId)} />}
      <button type="button" className="btn-icon" aria-label={`${workflow.isEnabled ? 'Disable' : 'Enable'} ${workflow.name}`}
        title={workflow.isEnabled ? 'Disable' : 'Enable'} onClick={onToggle}>{workflow.isEnabled ? '⏸' : '▶'}</button>
      <button type="button" className="btn-icon" aria-label={`Delete ${workflow.name}`} title="Delete" onClick={onDelete}>&#x1F5D1;&#xFE0F;</button>
    </div></td>
  </tr>;
};

const SharedSubWorkflowModal: React.FC<{
  workflow?: SubWorkflowDto;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (data: CreateSubWorkflowRequest | UpdateSubWorkflowRequest) => Promise<void>;
}> = ({ workflow, submitting, error, onClose, onSave }) => {
  const [name, setName] = useState(workflow?.name ?? '');
  const [description, setDescription] = useState(workflow?.description ?? '');
  const [sourceCompatibility, setSourceCompatibility] = useState<SubWorkflowDto['sourceCompatibility']>(workflow?.sourceCompatibility ?? 'Either');
  const [resultCodes, setResultCodes] = useState((workflow?.resultCodes ?? []).join('\n'));
  const [globalVariablesText, setGlobalVariablesText] = useState((workflow?.globalVariables ?? []).join('\n'));
  const [localError, setLocalError] = useState<string | null>(null);
  const isEdit = !!workflow;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const codes = resultCodes.split(/\r?\n/).map(code => code.trim()).filter(Boolean);
    const globalVariables = globalVariablesText.split(/\r?\n/).map(name => name.trim()).filter(Boolean);
    if (!name.trim()) { setLocalError('Name is required.'); return; }
    if (codes.some(code => code.length > 64)) { setLocalError('Each result code must be 64 characters or fewer.'); return; }
    if (codes.some(code => ['completed', 'failed', 'cancelled'].includes(code.toLowerCase()))) {
      setLocalError('Completed, Failed and Cancelled are reserved execution statuses.'); return;
    }
    if (new Set(codes.map(code => code.toLowerCase())).size !== codes.length) {
      setLocalError('Result codes must be unique ignoring case.'); return;
    }
    if (globalVariables.length > 50 || globalVariables.some(name => !/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(name))) {
      setLocalError('Global names must use 1-64 ASCII letters, digits, or underscores, start with a letter or underscore, and total at most 50.'); return;
    }
    if (new Set(globalVariables.map(name => name.toLowerCase())).size !== globalVariables.length) {
      setLocalError('Global variable names must be unique ignoring case.'); return;
    }
    setLocalError(null);
    await onSave({ name: name.trim(), description: description.trim(), sourceCompatibility,
      resultCodes: codes, globalVariables, ...(workflow ? { expectedDefinitionRevision: workflow.definitionRevision } : {}) } as CreateSubWorkflowRequest | UpdateSubWorkflowRequest);
  };

  return <div className="modal-overlay" onClick={onClose}>
    <form className="modal subworkflow-modal" aria-label={isEdit ? 'Edit shared sub-workflow' : 'New shared sub-workflow'}
      onClick={event => event.stopPropagation()} onSubmit={submit}>
      <h3>{isEdit ? 'Edit Sub-Workflow' : 'New Sub-Workflow'}</h3>
      <label>Name<input autoFocus required maxLength={255} value={name} onChange={event => setName(event.target.value)} /></label>
      <label>Description<textarea rows={3} value={description} onChange={event => setDescription(event.target.value)} /></label>
      <label>Source compatibility<select value={sourceCompatibility} onChange={event => setSourceCompatibility(event.target.value as SubWorkflowDto['sourceCompatibility'])}>
        <option value="Alert">Alert</option><option value="Notification">Notification</option><option value="Either">Either</option>
      </select></label>
      <label>Result codes<textarea rows={4} aria-label="Declared result codes" placeholder={'One code per line'} value={resultCodes}
        onChange={event => setResultCodes(event.target.value)} /></label>
      <label>Global variables<textarea rows={4} aria-label="Declared sub-workflow global variables" placeholder={'One name per line'} value={globalVariablesText}
        onChange={event => setGlobalVariablesText(event.target.value)} /></label>
      <p className="help-text">Values share the caller’s execution scope, start empty unless inherited from the parent, and are not persisted between runs.</p>
      {(localError || error) && <div className="error-message" role="alert">{localError || error}</div>}
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
        <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}</button>
      </div>
    </form>
  </div>;
};

// ─── Trigger Conditions Summary ─────────────────────────────────────────────

const TriggerConditionsSummary: React.FC<{ conditions: WorkflowTriggerConditions | null }> = ({ conditions }) => {
  if (!conditions) return <span style={{ color: 'var(--text-secondary, #888)' }}>Any</span>;
  const parts: string[] = [];
  if (conditions.severity?.length) parts.push(`Sev: ${conditions.severity.join(', ')}`);
  if (conditions.service?.length) parts.push(`Svc: ${conditions.service.join(', ')}`);
  if (conditions.source?.length) parts.push(`Src: ${conditions.source.join(', ')}`);
  if (parts.length === 0) return <span style={{ color: 'var(--text-secondary, #888)' }}>Any</span>;
  return (
    <div style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
      {parts.map((p, i) => <div key={i}>{p}</div>)}
    </div>
  );
};

// ─── Workflow Create/Edit Modal ─────────────────────────────────────────────

const WorkflowModal: React.FC<{
  workflow?: WorkflowDto;
  inboundIntegrations: InboundIntegration[];
  inboundError: string | null;
  onClose: () => void;
  onSave: (data: CreateWorkflowRequest) => Promise<void>;
}> = ({ workflow, inboundIntegrations, inboundError, onClose, onSave }) => {
  const isEdit = !!workflow;
  const [name, setName] = useState(workflow?.name || '');
  const [description, setDescription] = useState(workflow?.description || '');
  const [triggerType, setTriggerType] = useState(workflow?.triggerType || 'AlertCreated');
  const [inboundIntegrationId, setInboundIntegrationId] = useState(workflow?.inboundIntegrationId || '');
  const [existingSteps, setExistingSteps] = useState<WorkflowStepDto[]>([]);
  const [existingActions, setExistingActions] = useState<Record<string, OutboundIntegrationAction[]>>({});
  const [checkingSteps, setCheckingSteps] = useState(!!workflow);
  const [stepsError, setStepsError] = useState<string | null>(null);
  const [severities, setSeverities] = useState<string[]>(workflow?.triggerConditions?.severity || []);
  const [services, setServices] = useState(workflow?.triggerConditions?.service?.join(', ') || '');
  const [sources, setSources] = useState(workflow?.triggerConditions?.source?.join(', ') || '');
  const [globalVariableNames, setGlobalVariableNames] = useState<string[]>(workflow?.globalVariables ?? []);
  const [globalVariableDraft, setGlobalVariableDraft] = useState('');
  const [globalVariableError, setGlobalVariableError] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(workflow?.isEnabled ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workflow) return;
    let active = true;
    const load = async () => {
      try {
        const steps = await tenantApi.getWorkflowSteps(workflow.workflowId);
        const integrationIds = [...new Set(steps.filter(step => ['IntegrationAction', 'DispatchOutboundDelivery'].includes(step.stepType) && step.config?.integrationActionId)
          .map(step => String(step.config.integrationId)))];
        const actionEntries = await Promise.all(integrationIds.map(async id =>
          [id, (await tenantApi.getOutboundIntegrationActions(id)).filter(action => action.isEnabled)] as const));
        if (active) {
          setExistingSteps(steps);
          setExistingActions(Object.fromEntries(actionEntries));
        }
      } catch {
        if (active) setStepsError('Existing steps could not be checked. Close and reopen the editor to retry.');
      } finally {
        if (active) setCheckingSteps(false);
      }
    };
    load();
    return () => { active = false; };
  }, [workflow?.workflowId]);

  const source = { triggerType, inboundIntegrationId: inboundIntegrationId || null };
  const selectedIntegration = inboundIntegrations.find(item => item.inboundIntegrationId === inboundIntegrationId);
  const triggerChoices = getWorkflowTriggerChoices(selectedIntegration);
  const retainedTrigger = !triggerChoices.some(trigger => trigger.value === triggerType);
  const sourceError = (inboundError && (inboundIntegrationId || triggerType === 'NotificationReceived') ? inboundError : null) ||
    stepsError || validateWorkflowSource(source, inboundIntegrations, existingSteps, existingActions);

  const addGlobalVariable = () => {
    const name = globalVariableDraft.trim();
    if (!/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(name)) {
      setGlobalVariableError('Use 1-64 ASCII letters, digits, or underscores; the first character cannot be a digit.');
      return;
    }
    if (globalVariableNames.some(item => item.toLowerCase() === name.toLowerCase())) {
      setGlobalVariableError('Global variable names must be unique, ignoring case.');
      return;
    }
    if (globalVariableNames.length >= 50) {
      setGlobalVariableError('A workflow can define at most 50 global variables.');
      return;
    }
    setGlobalVariableNames(current => [...current, name]);
    setGlobalVariableDraft('');
    setGlobalVariableError(null);
  };

  const removeGlobalVariable = (name: string) => {
    const target = `global.${name}`.toLowerCase();
    const referenced = existingSteps.some(step =>
      Object.keys(step.config?.globalVariableMappings || {}).some(key => key.toLowerCase() === target));
    if (referenced) {
      setGlobalVariableError(`Remove the response capture for global.${name} before deleting its definition.`);
      return;
    }
    setGlobalVariableNames(current => current.filter(item => item !== name));
    setGlobalVariableError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required.'); return; }
    if (checkingSteps || sourceError) { setError(sourceError || 'Existing steps are still loading.'); return; }
    const pendingGlobalVariable = globalVariableDraft.trim();
    const globalVariablesToSave = pendingGlobalVariable
      ? [...globalVariableNames, pendingGlobalVariable]
      : globalVariableNames;
    if (pendingGlobalVariable && !/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(pendingGlobalVariable)) {
      setGlobalVariableError('Use 1-64 ASCII letters, digits, or underscores; the first character cannot be a digit.'); return;
    }
    if (pendingGlobalVariable && globalVariableNames.some(item => item.toLowerCase() === pendingGlobalVariable.toLowerCase())) {
      setGlobalVariableError('Global variable names must be unique, ignoring case.'); return;
    }
    if (globalVariablesToSave.length > 50) {
      setGlobalVariableError('A workflow can define at most 50 global variables.'); return;
    }
    if (globalVariablesToSave.some((item, index) => globalVariablesToSave.findIndex(name => name.toLowerCase() === item.toLowerCase()) !== index)) {
      setError('Global variable names must be unique, ignoring case.'); return;
    }
    setSubmitting(true);
    setError(null);
    const triggerConditions: WorkflowTriggerConditions = {};
    if (severities.length > 0) triggerConditions.severity = severities;
    if (services.trim()) triggerConditions.service = services.split(',').map(s => s.trim()).filter(Boolean);
    if (sources.trim()) triggerConditions.source = sources.split(',').map(s => s.trim()).filter(Boolean);
    const hasConditions = Object.keys(triggerConditions).length > 0;
    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        triggerType,
        ...getWorkflowAssociationChange(workflow?.inboundIntegrationId, inboundIntegrationId),
        triggerConditions: hasConditions ? triggerConditions : undefined,
        globalVariables: globalVariablesToSave,
        isEnabled,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg modal-scroll" onClick={(e) => e.stopPropagation()}>
        <h3>{isEdit ? 'Edit Workflow' : 'Create Workflow'}</h3>
        {error && <div className="error-message">{error}</div>}
        {sourceError && <div className="error-message" role="alert">{sourceError}</div>}
        {checkingSteps && <div className="loading">Checking existing steps...</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Notify On-Call for Critical Alerts" autoFocus />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What does this workflow do?" rows={2} />
          </div>
          <WorkflowInboundIntegrationSelect value={inboundIntegrationId} integrations={inboundIntegrations}
            onChange={setInboundIntegrationId} disabled={!!inboundError} />
          {inboundError && <div className="help-text" role="status">{inboundError}</div>}
          <div className="form-group">
            <label>Trigger</label>
            <select value={triggerType} onChange={(e) => setTriggerType(e.target.value)}>
              {retainedTrigger && <option value={triggerType} disabled>{TRIGGER_TYPES.find(trigger => trigger.value === triggerType)?.label || triggerType} (incompatible)</option>}
              {triggerChoices.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <fieldset style={{ border: '1px solid var(--border-color, #ddd)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
            <legend style={{ fontWeight: 600, fontSize: '0.9rem' }}>Trigger Conditions (optional)</legend>
            <div className="help-text" style={{ marginBottom: '0.5rem' }}>Leave empty to match all alerts.</div>
            <div className="form-group">
              <label>Severity</label>
              <div className="member-select-grid">
                {SEVERITY_OPTIONS.map(sev => (
                  <label key={sev} className="checkbox-label">
                    <input type="checkbox" checked={severities.includes(sev)} onChange={() => setSeverities(p => p.includes(sev) ? p.filter(s => s !== sev) : [...p, sev])} /> {sev}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Service (comma-separated)</label><input type="text" value={services} onChange={e => setServices(e.target.value)} /></div>
              <div className="form-group"><label>Source (comma-separated)</label><input type="text" value={sources} onChange={e => setSources(e.target.value)} /></div>
            </div>
          </fieldset>
          <fieldset className="workflow-global-variables">
            <legend>Workflow Global Variables</legend>
            <p className="help-text">Values start empty for each root run, are shared with awaited sub-workflows, and reset when that run ends. Runtime values are not saved to the workflow definition or application database.</p>
            <div className="form-row">
              <label htmlFor="workflow-global-variable-name">Variable name<input id="workflow-global-variable-name" value={globalVariableDraft}
                onChange={event => { setGlobalVariableDraft(event.target.value); setGlobalVariableError(null); }}
                onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); addGlobalVariable(); } }}
                placeholder="ticketId" /></label>
              <button type="button" className="btn-secondary" onClick={addGlobalVariable}><Plus size={16} /> Add variable</button>
            </div>
            {globalVariableNames.length > 0 && <ul aria-label="Defined workflow global variables">
              {globalVariableNames.map(variable => <li key={variable}>
                <code>global.{variable}</code>
                <button type="button" className="btn-icon" aria-label={`Remove global.${variable}`} title={`Remove global.${variable}`}
                  onClick={() => removeGlobalVariable(variable)}><Trash2 size={16} /></button>
              </li>)}
            </ul>}
            {globalVariableError && <p className="error-message" role="alert">{globalVariableError}</p>}
          </fieldset>
          <div className="form-group"><label className="checkbox-label"><input type="checkbox" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} /> Enabled</label></div>
          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || checkingSteps || !!sourceError}>{submitting ? 'Saving...' : 'Save and Edit Steps'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Steps Builder ──────────────────────────────────────────────────────────

const StepsBuilderContext = createContext<{
  state: BuilderState;
  dispatch: React.Dispatch<BuilderAction>;
  workflowId: string;
  globalVariableNames: string[];
  notificationSource: boolean;
  workflowIntegrationId?: string | null;
  isSubWorkflow: boolean;
  declaredResultCodes: string[];
  availableSubWorkflows: SubWorkflowDto[];
  refreshBuilderData: () => Promise<void>;
  channels: NotificationChannel[];
  channelSenders: ExchangeSenderRegistration[];
  users: User[];
  groups: Group[];
  schedules: Schedule[];
  integrations: Integration[];
  outboundIntegrations: OutboundIntegration[];
  outboundIntegrationActionsByIntegration: Record<string, OutboundIntegrationAction[]>;
  escalationPolicies: EscalationPolicyDto[];
  conditionMetadata: WorkflowConditionMetadata;
} | null>(null);

type BuilderState = {
  steps: WorkflowStepDto[];
  isDirty: boolean;
  isValid: boolean;
};

type BuilderAction =
  | { type: 'LOAD_STEPS'; payload: WorkflowStepDto[] }
  | { type: 'ADD_STEP'; payload: { index: number; type: string } }
  | { type: 'REMOVE_STEP'; payload: number }
  | { type: 'UPDATE_STEP'; payload: { index: number; changes: Partial<WorkflowStepDto> } }
  | { type: 'UPDATE_CONFIG'; payload: { index: number; configChanges: any } }
  | { type: 'REORDER_STEPS'; payload: { sourceIndex: number; destIndex: number } }
  | { type: 'SET_CLEAN' };

const validateSteps = (steps: WorkflowStepDto[]): boolean => {
  return steps.every(step => {
    if (!step.name.trim()) return false;
    // Add more step-specific validation here if needed
    return true;
  });
};

const builderReducer = (state: BuilderState, action: BuilderAction): BuilderState => {
  let newSteps: WorkflowStepDto[] = [];
  switch (action.type) {
    case 'LOAD_STEPS':
    {
      const modelKeys = (value: Record<string, unknown>) => Object.fromEntries(Object.entries(value)
        .map(([key, item]) => [key.slice(0, 1).toLowerCase() + key.slice(1), item]));
      const loadedSteps = action.payload.map(step => {
        const snapshot = step.config?.definitionSnapshot ?? step.config?.DefinitionSnapshot;
        if (step.stepType !== 'IntegrationAction' || !snapshot || typeof snapshot !== 'object') return step;
        const config = modelKeys(step.config);
        config.definitionSnapshot = modelKeys(snapshot);
        return { ...step, config };
      });
      return { steps: loadedSteps, isDirty: false, isValid: validateSteps(loadedSteps) };
    }
    case 'ADD_STEP':
    {
      const newStep = {
        stepId: crypto.randomUUID(),
        workflowId: '', // Will be set on save
        stepOrder: action.payload.index,
        stepType: action.payload.type,
        name: STEP_TYPES.find(t => t.value === action.payload.type)?.label || 'New Step',
        config: getDefaultConfig(action.payload.type),
        continueOnError: false,
      };
      newSteps = [...state.steps];
      newSteps.splice(action.payload.index, 0, newStep);
      newSteps = newSteps.map((s, i) => ({ ...s, stepOrder: i }));
      break;
    }
    case 'REMOVE_STEP':
      newSteps = state.steps.filter((_, i) => i !== action.payload).map((s, i) => ({ ...s, stepOrder: i }));
      break;
    case 'UPDATE_STEP':
      newSteps = state.steps.map((s, i) => i === action.payload.index ? { ...s, ...action.payload.changes } : s);
      break;
    case 'UPDATE_CONFIG':
      newSteps = state.steps.map((step, index) => {
        if (index !== action.payload.index) return step;
        const changes = action.payload.configChanges;
        const bindingChanged = ['integrationId', 'integrationActionId'].some(key =>
          Object.prototype.hasOwnProperty.call(changes, key) && changes[key] !== step.config[key]);
        const config = { ...step.config, ...changes };
        if (bindingChanged && !Object.prototype.hasOwnProperty.call(changes, 'definitionSnapshot')) {
          delete config.definitionSnapshot;
          delete config.integrationVersion;
        }
        return { ...step, config };
      });
      break;
    case 'REORDER_STEPS':
      newSteps = [...state.steps];
    {
      const [moved] = newSteps.splice(action.payload.sourceIndex, 1);
      newSteps.splice(action.payload.destIndex, 0, moved);
      newSteps = newSteps.map((s, i) => ({ ...s, stepOrder: i }));
      break;
    }
    case 'SET_CLEAN':
      return { ...state, isDirty: false };
    default:
      return state;
  }
  return { steps: newSteps, isDirty: true, isValid: validateSteps(newSteps) };
};

const GlobalVariableMappingsEditor: React.FC<{
  globalVariableNames: string[];
  allowInherited: boolean;
  mappings: Record<string, string> | null | undefined;
  onChange: (mappings: Record<string, string>) => void;
}> = ({ globalVariableNames, allowInherited, mappings: providedMappings, onChange }) => {
  const mappings = providedMappings ?? {};
  const entries = Object.entries(mappings);
  const addMapping = () => {
    const used = new Set(Object.keys(mappings).map(key => key.toLowerCase()));
    const next = globalVariableNames.find(name => !used.has(`global.${name}`.toLowerCase()));
    if (next) onChange({ ...mappings, [`global.${next}`]: '' });
    else if (allowInherited) onChange({ ...mappings, [`global.inherited${entries.length + 1}`]: '' });
  };
  const updateMapping = (target: string, name: string, pointer: string) => {
    const next = { ...mappings };
    const nextTarget = allowInherited ? name : (name ? `global.${name}` : target);
    if (!allowInherited && !/^global\.[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(nextTarget)) return;
    if (allowInherited && !nextTarget) return;
    if (Object.keys(next).some(key => key !== target && key.toLowerCase() === nextTarget.toLowerCase())) return;
    if (nextTarget !== target) delete next[target];
    next[nextTarget] = pointer;
    onChange(next);
  };
  const availableNames = (target: string) => globalVariableNames.filter(name =>
    `global.${name}`.toLowerCase() === target.toLowerCase() ||
    !Object.keys(mappings).some(key => key.toLowerCase() === `global.${name}`.toLowerCase()));

  return (
    <div className="guide-callout workflow-global-capture" aria-label="Capture response to workflow globals">
      <strong>Capture response to workflow globals</strong>
      <p className="help-text">Map JSON Pointer response fields to declared names. Values are shared with awaited sub-workflows and reset for each root run; only the names are saved.</p>
      {globalVariableNames.length === 0 && <p className="help-text">Define global variable names in Edit Workflow before adding response captures.</p>}
      {allowInherited && <p className="help-text">Inherited caller globals can be entered by name. The run is rejected before sending if the caller has not declared that name.</p>}
      {entries.map(([target, pointer], index) => (
        <div className="form-row" key={target}>
          <label>{`Global variable ${index + 1}`}
            {allowInherited
              ? <input aria-label={`Global response target ${index + 1}`} list={`global-response-targets-${index}`} value={target}
                  onChange={event => updateMapping(target, event.target.value, pointer)} />
              : <select aria-label={`Global response target ${index + 1}`} value={target}
                  onChange={event => updateMapping(target, event.target.value.replace(/^global\./i, ''), pointer)}>
                  <option value={target} disabled={!globalVariableNames.includes(target.replace(/^global\./i, ''))}>{target} (undefined)</option>
                  {availableNames(target).map(name => <option key={name} value={`global.${name}`}>{`global.${name}`}</option>)}
                </select>}
            {allowInherited && <datalist id={`global-response-targets-${index}`}>
              {globalVariableNames.map(name => <option key={name} value={`global.${name}`} />)}
            </datalist>}
          </label>
          <label>{`JSON Pointer ${index + 1}`}
            <input aria-label={`Global response pointer ${index + 1}`} value={pointer}
              onChange={event => updateMapping(target, target.replace(/^global\./i, ''), event.target.value)} placeholder="/ticket/id" />
          </label>
          <button type="button" className="btn-icon" aria-label={`Remove response capture ${target}`} title={`Remove ${target}`}
            onClick={() => { const next = { ...mappings }; delete next[target]; onChange(next); }}><Trash2 size={16} /></button>
        </div>
      ))}
      <button type="button" className="btn-secondary btn-sm" disabled={!allowInherited && !globalVariableNames.some(name =>
        !Object.keys(mappings).some(key => key.toLowerCase() === `global.${name}`.toLowerCase()))} onClick={addMapping}>
        <Plus size={15} /> Add response capture
      </button>
    </div>
  );
};


const StepsBuilderModal: React.FC<{
  workflow: WorkflowDto;
  subWorkflowSource?: SubWorkflowDto['sourceCompatibility'];
  subWorkflowScope?: SubWorkflowDto['scope'];
  ownerWorkflow?: WorkflowDto;
  expectedDefinitionRevision?: number;
  declaredResultCodes?: string[];
  inboundIntegrations: InboundIntegration[];
  inboundError: string | null;
  onClose: () => void;
}> = ({ workflow, subWorkflowSource, subWorkflowScope, ownerWorkflow, expectedDefinitionRevision, declaredResultCodes = [], inboundIntegrations, inboundError, onClose }) => {
  const { ask, dialog } = useWorkflowDialog();
  const [state, dispatch] = useReducer(builderReducer, { steps: [], isDirty: false, isValid: true });
  const stepsPanelRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const backdropPressedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showNotificationGuide, setShowNotificationGuide] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const pendingStepReload = useRef<(() => void) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [channelSenders, setChannelSenders] = useState<ExchangeSenderRegistration[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [outboundIntegrations, setOutboundIntegrations] = useState<OutboundIntegration[]>([]);
  const [outboundIntegrationActionsByIntegration, setOutboundIntegrationActionsByIntegration] = useState<Record<string, OutboundIntegrationAction[]>>({});
  const [escalationPolicies, setEscalationPolicies] = useState<EscalationPolicyDto[]>([]);
  const [conditionMetadata, setConditionMetadata] = useState<WorkflowConditionMetadata>({
    fields: DEFAULT_CONDITION_FIELD_OPTIONS.map((f) => ({ key: f.value, label: f.label, category: f.category })),
    operators: DEFAULT_CONDITION_OPERATORS.map((o) => ({ key: o.value, label: o.label, hint: o.hint })),
    customFieldPrefix: 'alert.data.',
    notes: 'Field keys are matched case-insensitively. Use alert.data.<key> for integration payload fields.',
  });
  const [workflowSecrets, setWorkflowSecrets] = useState<WorkflowSecret[]>([]);
  const [secretName, setSecretName] = useState('');
  const [secretValue, setSecretValue] = useState('');
  const [secretBusy, setSecretBusy] = useState(false);
  const [secretError, setSecretError] = useState<string | null>(null);
  const [privateManagerOpen, setPrivateManagerOpen] = useState(false);
  const [privateSubWorkflows, setPrivateSubWorkflows] = useState<SubWorkflowDto[]>([]);
  const [sharedSubWorkflows, setSharedSubWorkflows] = useState<SubWorkflowDto[]>([]);
  const [availableSubWorkflows, setAvailableSubWorkflows] = useState<SubWorkflowDto[]>([]);
  const [privateName, setPrivateName] = useState('');
  const [privateDescription, setPrivateDescription] = useState('');
  const [privateCodes, setPrivateCodes] = useState('');
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [privateBusy, setPrivateBusy] = useState(false);
  const [privateError, setPrivateError] = useState<string | null>(null);
  const [nestedPrivate, setNestedPrivate] = useState<SubWorkflowDto | null>(null);
  const [nestedPrivateOwner, setNestedPrivateOwner] = useState<WorkflowDto | null>(null);
  const privateOwner = workflow;
  const notificationSource = subWorkflowSource === 'Notification' || (!subWorkflowSource && isNotificationWorkflow(workflow,
    inboundIntegrations.find(integration => integration.inboundIntegrationId === workflow.inboundIntegrationId)));
  const sourceError = subWorkflowSource
    ? notificationSource && state.steps.some(step => !isNotificationSafeStep(step.stepType))
      ? 'Notification-compatible sub-workflows cannot use UpdateAlert or Escalate.' : null
    : (inboundError && (workflow.inboundIntegrationId || notificationSource) ? inboundError : null) ||
      validateWorkflowSource(workflow, inboundIntegrations, state.steps, outboundIntegrationActionsByIntegration);

  const refreshBuilderData = useCallback(async () => {
    const [stepsData, outboundData] = await Promise.all([
      tenantApi.getWorkflowSteps(workflow.workflowId),
      tenantApi.getOutboundIntegrations().catch(() => []),
    ]);

    dispatch({ type: 'LOAD_STEPS', payload: stepsData });

    const outboundList = (Array.isArray(outboundData) ? outboundData : []).map((i: any) => ({
      ...i,
      outboundIntegrationId: i.outboundIntegrationId || i.OutboundIntegrationId,
      name: i.name || i.Name,
      provider: i.provider || i.Provider,
      isEnabled: (i.isEnabled ?? i.IsEnabled ?? true) as boolean,
    }));
    setOutboundIntegrations(outboundList);

    const actionResults = await Promise.all(outboundList.map(async (integration) => {
      try {
        const actions = await tenantApi.getOutboundIntegrationActions(integration.outboundIntegrationId);
        return [integration.outboundIntegrationId, Array.isArray(actions) ? actions : []] as const;
      } catch {
        return [integration.outboundIntegrationId, []] as const;
      }
    }));

    const actionMap: Record<string, OutboundIntegrationAction[]> = {};
    actionResults.forEach(([integrationId, actions]) => {
      actionMap[integrationId] = actions.filter(a => a.isEnabled);
    });
    setOutboundIntegrationActionsByIntegration(actionMap);
  }, [workflow.workflowId]);

  useEffect(() => {
    let active = true;
    tenantApi.getSharedSubWorkflows()
      .then((shared) => {
        if (active) setAvailableSubWorkflows(shared);
      })
      .catch(() => { if (active) setAvailableSubWorkflows([]); });
    return () => { active = false; };
  }, [workflow.workflowId]);

  useEffect(() => {
    if (!PRIVATE_SUBWORKFLOW_UI_ENABLED || !privateManagerOpen) return;
    let active = true;
    setPrivateBusy(true);
    Promise.all([
      tenantApi.getPrivateSubWorkflows(privateOwner.workflowId),
      tenantApi.getSharedSubWorkflows(),
    ]).then(([privateData, sharedData]) => {
      if (!active) return;
      setPrivateSubWorkflows(privateData);
      setSharedSubWorkflows(sharedData);
      if (!cloneSourceId && sharedData.length) setCloneSourceId(sharedData[0].workflowId);
    }).catch(err => {
      if (active) setPrivateError(err instanceof Error ? err.message : 'Private sub-workflows could not be loaded.');
    }).finally(() => { if (active) setPrivateBusy(false); });
    return () => { active = false; };
  }, [privateManagerOpen, privateOwner.workflowId]);

  const openPrivateBuilder = (definition: SubWorkflowDto) => {
    setPrivateManagerOpen(false);
    setNestedPrivate(definition);
  };

  const createPrivateDefinition = async (cloneShared: boolean) => {
    if (state.isDirty) { setPrivateError('Save or discard parent step changes before managing private sub-workflows.'); return; }
    if (!privateName.trim()) { setPrivateError('A private sub-workflow name is required.'); return; }
    const codes = privateCodes.split(/\r?\n/).map(code => code.trim()).filter(Boolean);
    setPrivateBusy(true);
    setPrivateError(null);
    try {
      const definition = cloneShared
        ? await tenantApi.cloneSharedSubWorkflowAsPrivate(privateOwner.workflowId, cloneSourceId, privateName.trim())
        : await tenantApi.createPrivateSubWorkflow(privateOwner.workflowId, {
            name: privateName.trim(), description: privateDescription.trim(), resultCodes: codes,
          });
      setPrivateSubWorkflows(current => [...current.filter(item => item.workflowId !== definition.workflowId), definition]);
      setPrivateName('');
      setPrivateDescription('');
      setPrivateCodes('');
      openPrivateBuilder(definition);
    } catch (err) {
      setPrivateError(err instanceof Error ? err.message : 'Private sub-workflow could not be created.');
    } finally {
      setPrivateBusy(false);
    }
  };

  const deletePrivateDefinition = async (definition: SubWorkflowDto) => {
    if (await ask({ title: 'Delete private sub-workflow?', message: `Delete private sub-workflow "${definition.name}"?`, action: 'Delete sub-workflow', destructive: true }) === null) return;
    setPrivateBusy(true);
    setPrivateError(null);
    try {
      await tenantApi.deletePrivateSubWorkflow(privateOwner.workflowId, definition.workflowId);
      setPrivateSubWorkflows(current => current.filter(item => item.workflowId !== definition.workflowId));
    } catch (err) {
      setPrivateError(err instanceof Error ? err.message : 'Private sub-workflow could not be deleted.');
    } finally { setPrivateBusy(false); }
  };

  const convertPrivateToShared = async () => {
    if (subWorkflowScope !== 'Private' || !ownerWorkflow) return;
    if (state.isDirty) { setError('Save or discard step changes before converting this definition.'); return; }
    if (expectedDefinitionRevision == null) { setError('Reload this private definition before converting it.'); return; }
    const name = await ask({ title: 'Convert to shared sub-workflow', message: 'Enter a name for the shared sub-workflow.', action: 'Convert to shared', defaultValue: workflow.name });
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const latestOwner = await tenantApi.getWorkflow(ownerWorkflow.workflowId);
      await tenantApi.convertPrivateSubWorkflowToShared(ownerWorkflow.workflowId, workflow.workflowId, {
        name, expectedDefinitionRevision,
        expectedOwnerUpdatedAt: latestOwner.updatedAt,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Private sub-workflow could not be converted.');
    } finally { setSaving(false); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [stepsData, channelsData, usersData, groupsData, schedulesData, integrationsData, outboundData, escalationPoliciesData, conditionMetadataData, workflowSecretsData, channelSenderData] = await Promise.all([
          tenantApi.getWorkflowSteps(workflow.workflowId),
          tenantApi.getNotificationChannels(),
          tenantApi.getUsers({ take: 1000 }), // Fetch all users
          tenantApi.getGroups().catch(() => []),
          tenantApi.getSchedules().catch(() => []),
          tenantApi.getIntegrations(),
          tenantApi.getOutboundIntegrations().catch(() => []),
          tenantApi.getEscalationPolicies(),
          tenantApi.getWorkflowConditionMetadata().catch(() => ({
            fields: DEFAULT_CONDITION_FIELD_OPTIONS.map((f) => ({ key: f.value, label: f.label, category: f.category })),
            operators: DEFAULT_CONDITION_OPERATORS.map((o) => ({ key: o.value, label: o.label, hint: o.hint })),
            customFieldPrefix: 'alert.data.',
            notes: 'Field keys are matched case-insensitively. Use alert.data.<key> for integration payload fields.',
          })),
          tenantApi.getWorkflowSecrets().catch(() => []),
          tenantApi.getEligibleExchangeSenders().catch(() => []),
        ]);
        dispatch({ type: 'LOAD_STEPS', payload: stepsData });
        setChannels(Array.isArray(channelsData) ? channelsData : []);
        setChannelSenders(Array.isArray(channelSenderData) ? channelSenderData : []);
        setUsers(Array.isArray(usersData?.users) ? usersData.users : Array.isArray(usersData) ? usersData : []);
        setGroups(Array.isArray(groupsData) ? groupsData : []);
        setSchedules(Array.isArray(schedulesData) ? schedulesData : []);
        setIntegrations(Array.isArray(integrationsData) ? integrationsData : []);
        const outboundList = (Array.isArray(outboundData) ? outboundData : []).map((i: any) => ({
          ...i,
          outboundIntegrationId: i.outboundIntegrationId || i.OutboundIntegrationId,
          name: i.name || i.Name,
          provider: i.provider || i.Provider,
          isEnabled: (i.isEnabled ?? i.IsEnabled ?? true) as boolean,
        }));
        setOutboundIntegrations(outboundList);
        const actionResults = await Promise.all(outboundList.map(async (integration) => {
          try {
            const actions = await tenantApi.getOutboundIntegrationActions(integration.outboundIntegrationId);
            return [integration.outboundIntegrationId, Array.isArray(actions) ? actions : []] as const;
          } catch {
            return [integration.outboundIntegrationId, []] as const;
          }
        }));
        const actionMap: Record<string, OutboundIntegrationAction[]> = {};
        actionResults.forEach(([integrationId, actions]) => {
          actionMap[integrationId] = actions.filter(a => a.isEnabled);
        });
        setOutboundIntegrationActionsByIntegration(actionMap);
        setEscalationPolicies(Array.isArray(escalationPoliciesData) ? escalationPoliciesData : []);
        if (conditionMetadataData && Array.isArray(conditionMetadataData.fields) && Array.isArray(conditionMetadataData.operators)) {
          setConditionMetadata(conditionMetadataData);
        }
        setWorkflowSecrets(Array.isArray(workflowSecretsData) ? workflowSecretsData : []);
      } catch (err: any) { setError(err.message); }
      finally { setLoading(false); }
    };
    if (state.isDirty) {
      pendingStepReload.current = () => { void load(); };
      setConfirmDiscard(true);
    } else {
      void load();
    }
  }, [workflow.workflowId]);

  const handleSaveSecret = async () => {
    const name = secretName.trim();
    if (!name) {
      setSecretError('Secret name is required.');
      return;
    }
    if (!secretValue.trim()) {
      setSecretError('Secret value is required.');
      return;
    }

    setSecretBusy(true);
    setSecretError(null);
    try {
      await tenantApi.upsertWorkflowSecret(name, secretValue);
      const refreshed = await tenantApi.getWorkflowSecrets();
      setWorkflowSecrets(Array.isArray(refreshed) ? refreshed : []);
      setSecretValue('');
      setSecretName('');
    } catch (err: any) {
      setSecretError(err?.message || 'Failed to store workflow secret');
    } finally {
      setSecretBusy(false);
    }
  };

  const handleDeleteSecret = async (name: string) => {
    if (await ask({ title: 'Delete workflow secret?', message: `Delete workflow secret '${name}'?`, action: 'Delete secret', destructive: true }) === null) return;
    setSecretBusy(true);
    setSecretError(null);
    try {
      await tenantApi.deleteWorkflowSecret(name);
      setWorkflowSecrets(prev => prev.filter(s => s.name !== name));
    } catch (err: any) {
      setSecretError(err?.message || 'Failed to delete workflow secret');
    } finally {
      setSecretBusy(false);
    }
  };

  const handleClose = () => {
    if (draggingRef.current) return;
    if (saving) return;
    if (state.isDirty) {
      pendingStepReload.current = null;
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const discardAndClose = () => {
    setConfirmDiscard(false);
    const reload = pendingStepReload.current;
    pendingStepReload.current = null;
    if (reload) reload();
    else onClose();
  };

  const keepEditing = () => {
    pendingStepReload.current = null;
    setConfirmDiscard(false);
  };

  const handleSave = async () => {
    if (!state.isValid) { setError('One or more steps are invalid. Please check for empty names.'); return; }
    if (sourceError) { setError(sourceError); return; }
    setSaving(true);
    setError(null);
    try {
      const stepsToSave: WorkflowStepRequest[] = state.steps.map(s => ({
        stepId: s.stepId,
        stepOrder: s.stepOrder,
        stepType: s.stepType,
        name: s.name,
        config: s.config,
        continueOnError: s.continueOnError,
      }));
      await tenantApi.saveWorkflowSteps(workflow.workflowId, stepsToSave, expectedDefinitionRevision);
      dispatch({ type: 'SET_CLEAN' });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const onDragEnd = (result: DropResult) => {
    draggingRef.current = false;
    backdropPressedRef.current = false;
    if (!result.destination || result.reason === 'CANCEL' || result.source.index === result.destination.index) return;
    dispatch({ type: 'REORDER_STEPS', payload: { sourceIndex: result.source.index, destIndex: result.destination.index } });
  };

  return (
    <>
    <StepsBuilderContext.Provider value={{ state, dispatch, workflowId: workflow.workflowId,
      globalVariableNames: workflow.globalVariables ?? [], notificationSource,
      workflowIntegrationId: workflow.inboundIntegrationId, isSubWorkflow: !!subWorkflowSource, declaredResultCodes,
      availableSubWorkflows, refreshBuilderData, channels, channelSenders, users, groups, schedules, integrations, outboundIntegrations,
      outboundIntegrationActionsByIntegration, escalationPolicies, conditionMetadata }}>
      {createPortal(<div className="modal-overlay workflow-steps-overlay"
        onMouseDown={event => { backdropPressedRef.current = event.button === 0 && event.target === event.currentTarget && !draggingRef.current; }}
        onMouseUp={event => {
          const close = shouldCloseStepBackdrop(backdropPressedRef.current, event.button === 0 && event.target === event.currentTarget, draggingRef.current);
          backdropPressedRef.current = false;
          if (close) handleClose();
        }}>
        <div className="modal workflow-steps-modal" onClick={(e) => e.stopPropagation()}>
          <div className="workflow-steps-heading">
            <h3>{subWorkflowSource ? `Sub-workflow steps: ${workflow.name}` : `Steps: ${workflow.name}`}</h3>
            <button type="button" className="btn-secondary btn-sm" aria-expanded={showNotificationGuide}
              aria-controls="workflow-notification-howto" onClick={() => setShowNotificationGuide(value => !value)}>
              <CircleHelp size={16} /> HowTo
            </button>
          </div>
          <div className="workflow-subworkflow-tools">
            {PRIVATE_SUBWORKFLOW_UI_ENABLED && <button type="button" className="btn-secondary btn-sm" disabled={state.isDirty || loading}
              title={state.isDirty ? 'Save or discard step changes first' : undefined}
              onClick={() => { setPrivateError(null); setPrivateManagerOpen(value => !value); }}>
              {privateManagerOpen ? 'Hide private sub-workflows' : `Private sub-workflows (${privateSubWorkflows.length})`}
            </button>}
            {PRIVATE_SUBWORKFLOW_UI_ENABLED && subWorkflowScope === 'Private' && ownerWorkflow && <button type="button" className="btn-secondary btn-sm"
              disabled={state.isDirty || saving} onClick={convertPrivateToShared}>Convert to shared</button>}
          </div>
          {error && <div className="error-message">{error}</div>}
          {!loading && sourceError && <div className="error-message" role="alert">{sourceError}</div>}
          {PRIVATE_SUBWORKFLOW_UI_ENABLED && privateManagerOpen && <section className="private-subworkflow-manager" aria-label="Private sub-workflows">
            <div className="private-subworkflow-manager-header"><h4>Private sub-workflows</h4>
              <span>Owned by {privateOwner.name}</span></div>
            {privateError && <div className="error-message" role="alert">{privateError}</div>}
            <div className="private-subworkflow-create">
              <label>Name<input aria-label="Private sub-workflow name" value={privateName} onChange={event => setPrivateName(event.target.value)} /></label>
              <label>Description<input aria-label="Private sub-workflow description" value={privateDescription} onChange={event => setPrivateDescription(event.target.value)} /></label>
              <label>Result codes<textarea aria-label="Private sub-workflow result codes" rows={2} value={privateCodes}
                onChange={event => setPrivateCodes(event.target.value)} placeholder="One code per line" /></label>
              <button type="button" className="btn-secondary btn-sm" disabled={privateBusy || !privateName.trim()}
                onClick={() => createPrivateDefinition(false)}>Create blank</button>
            </div>
            <div className="private-subworkflow-clone">
              <label>Clone shared<select aria-label="Shared sub-workflow to clone" value={cloneSourceId} onChange={event => setCloneSourceId(event.target.value)}>
                <option value="">Select shared sub-workflow</option>
                {sharedSubWorkflows.map(definition => <option key={definition.workflowId} value={definition.workflowId}>{definition.name}</option>)}
              </select></label>
              <button type="button" className="btn-secondary btn-sm" disabled={privateBusy || !privateName.trim() || !cloneSourceId}
                onClick={() => createPrivateDefinition(true)}>Clone as private</button>
            </div>
            {privateBusy && <div className="loading">Loading or saving private definitions...</div>}
            <ul className="private-subworkflow-list">
              {privateSubWorkflows.map(definition => <li key={definition.workflowId}>
                <span><strong>{definition.name}</strong>{definition.clonedFromWorkflowId && <small>Cloned from shared definition</small>}</span>
                <span className="private-subworkflow-actions">
                  <button type="button" className="btn-secondary btn-sm" onClick={() => openPrivateBuilder(definition)}>Edit steps</button>
                  <ResourceCloneButton source={{ rootKind: 'workflow', rootId: definition.workflowId, name: definition.name, requiresOwner: true, isSubWorkflow: true }}
                    onCreated={async () => {
                      setPrivateSubWorkflows(await tenantApi.getPrivateSubWorkflows(privateOwner.workflowId));
                    }} onOpen={async (result, destinationOwnerId) => {
                      if (!destinationOwnerId) throw new Error('A destination workflow is required to open this private clone.');
                      const definitions = await tenantApi.getPrivateSubWorkflows(destinationOwnerId);
                      const created = definitions.find(row => row.workflowId === result.rootId);
                      if (!created) throw new Error('The cloned private definition is unavailable. Reload the destination workflow.');
                      setNestedPrivateOwner(await tenantApi.getWorkflow(destinationOwnerId));
                      setNestedPrivate(created);
                    }} />
                  <button type="button" className="btn-icon" aria-label={`Delete ${definition.name}`} title="Delete private definition"
                    onClick={() => deletePrivateDefinition(definition)}><Trash2 size={15} /></button>
                </span>
              </li>)}
              {!privateBusy && privateSubWorkflows.length === 0 && <li className="subworkflow-muted">No private definitions yet.</li>}
            </ul>
          </section>}
          <details className="workflow-secrets-panel" style={{ marginBottom: '0.8rem' }}>
            <summary>Workflow Secrets (optional)</summary>
            <div className="help-text" style={{ margin: '0.45rem 0' }}>
              Store tenant-scoped workflow secrets and reference them as <code>{'{{secret.name}}'}</code>. This is optional and separate from workflow step configuration.
            </div>
            {secretError && <div className="error-message" style={{ marginBottom: '0.5rem' }}>{secretError}</div>}
            <div className="form-row">
              <div className="form-group">
                <label>Secret Name</label>
                <input
                  type="text"
                  value={secretName}
                  onChange={e => setSecretName(e.target.value)}
                  placeholder="api_token"
                />
              </div>
              <div className="form-group">
                <label>Secret Value</label>
                <input
                  type="password"
                  value={secretValue}
                  onChange={e => setSecretValue(e.target.value)}
                  placeholder="Paste secret value"
                />
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: 0 }}>
              <button type="button" className="btn-secondary" disabled={secretBusy} onClick={handleSaveSecret}>
                {secretBusy ? 'Saving...' : 'Save Secret'}
              </button>
            </div>
            <div style={{ marginTop: '0.45rem', maxHeight: 120, overflowY: 'auto' }}>
              {workflowSecrets.length === 0 ? (
                <div className="help-text">No workflow secrets saved yet.</div>
              ) : (
                workflowSecrets.map(secret => (
                  <div key={secret.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0.2rem 0' }}>
                    <code>{secret.token}</code>
                    <button
                      type="button"
                      className="btn-icon"
                      title="Delete secret"
                      disabled={secretBusy}
                      onClick={() => handleDeleteSecret(secret.name)}
                    >
                      &#x1F5D1;&#xFE0F;
                    </button>
                  </div>
                ))
              )}
            </div>
          </details>
          <div className="workflow-steps-body" ref={stepsPanelRef}>
            {showNotificationGuide && <section id="workflow-notification-howto" className="workflow-notification-howto" aria-label="Notification message guide">
              <WorkflowNotificationGuideContent />
            </section>}
            {loading ? ( <div className="loading">Loading...</div> ) : (
              <>
                <DragDropContext onBeforeDragStart={() => { draggingRef.current = true; backdropPressedRef.current = false; }} onDragEnd={onDragEnd}>
                  <Droppable droppableId="steps">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {state.steps.length === 0 && (
                          <div className="workflow-step-empty">
                            No steps yet. Add one below.
                          </div>
                        )}
                        {state.steps.map((step, index) => (
                          <Draggable key={step.stepId} draggableId={step.stepId} index={index}>
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.draggableProps}
                                style={snapshot.isDragging ? clampStepDragStyle(provided.draggableProps.style, stepsPanelRef.current?.getBoundingClientRect() || null) : provided.draggableProps.style}>
                                <AddStepButton index={index} />
                                <StepCard step={step} index={index} dragHandleProps={provided.dragHandleProps} />
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <AddStepButton index={state.steps.length} isLast />
              </>
            )}
          </div>
          <div className="form-actions" style={{ marginTop: 'auto', paddingTop: '1rem' }}>
            <button type="button" className="btn-secondary" onClick={handleClose}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || loading || !state.isDirty || !state.isValid || !!sourceError}>
              {saving ? 'Saving...' : state.isDirty ? 'Save Steps' : 'Saved'}
            </button>
          </div>
        </div>
      </div>, document.body)}
    </StepsBuilderContext.Provider>
    {dialog}
    {confirmDiscard && createPortal(<div className="modal-overlay workflow-unsaved-confirmation-overlay"
      onMouseDown={event => { if (event.target === event.currentTarget) keepEditing(); }}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          keepEditing();
        }
      }}>
      <div className="modal modal-sm workflow-unsaved-confirmation" role="alertdialog" aria-modal="true"
        aria-labelledby="workflow-unsaved-confirmation-title" aria-describedby="workflow-unsaved-confirmation-message"
        onClick={event => event.stopPropagation()}>
        <h3 id="workflow-unsaved-confirmation-title">Discard unsaved changes?</h3>
        <p id="workflow-unsaved-confirmation-message">Your workflow step changes have not been saved.</p>
        <div className="form-actions">
          <button type="button" className="btn-secondary" autoFocus onClick={keepEditing}>Keep editing</button>
          <button type="button" className="btn-danger" onClick={discardAndClose}>Discard changes</button>
        </div>
      </div>
    </div>, document.body)}
    {PRIVATE_SUBWORKFLOW_UI_ENABLED && nestedPrivate && <StepsBuilderModal workflow={toWorkflowDto(nestedPrivate)}
      subWorkflowSource={nestedPrivate.sourceCompatibility} subWorkflowScope="Private"
      declaredResultCodes={nestedPrivate.resultCodes}
      ownerWorkflow={nestedPrivateOwner ?? privateOwner} expectedDefinitionRevision={nestedPrivate.definitionRevision}
      inboundIntegrations={inboundIntegrations} inboundError={inboundError}
      onClose={() => {
        setNestedPrivate(null);
        setNestedPrivateOwner(null);
        setPrivateManagerOpen(true);
        void refreshBuilderData();
      }} />}
    </>
  );
};

const AddStepButton: React.FC<{ index: number, isLast?: boolean }> = ({ index, isLast }) => {
  const { dispatch, notificationSource } = useContext(StepsBuilderContext)!;
  const [isOpen, setIsOpen] = useState(false);

  const addStep = (type: string) => {
    dispatch({ type: 'ADD_STEP', payload: { index, type } });
    setIsOpen(false);
  };

  return (
    <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', margin: isLast ? '0.5rem 0' : '-8px 0' }}>
      <button className="btn-secondary btn-sm" onClick={() => setIsOpen(p => !p)} style={{ zIndex: 1 }}>+ Add Step</button>
      {isOpen && (
        <div className="workflow-add-step-menu" style={{ position: 'absolute', top: '100%', zIndex: 10, display: 'flex', flexDirection: 'column', marginTop: 4 }}>
          {STEP_TYPES.map(t => (
            <button key={t.value} onClick={() => addStep(t.value)} className="workflow-add-step-menu-item"
              disabled={notificationSource && !isNotificationSafeStep(t.value)}
              title={notificationSource && !isNotificationSafeStep(t.value) ? 'Not supported for notification workflows' : undefined}>
              {t.label}
            </button>
          ))}
        </div>
      )}
      {!isLast && <div className="workflow-step-divider" style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1 }} />}
    </div>
  );
};

const StepCard: React.FC<{ step: WorkflowStepDto, index: number, dragHandleProps: DraggableProvidedDragHandleProps | null | undefined }> = ({ step, index, dragHandleProps }) => {
  const { ask, dialog } = useWorkflowDialog();
  const { dispatch, state, workflowIntegrationId, notificationSource } = useContext(StepsBuilderContext)!;
  const update = (changes: Partial<WorkflowStepDto>) => dispatch({ type: 'UPDATE_STEP', payload: { index, changes } });
  const updateConfig = (configChanges: any) => dispatch({ type: 'UPDATE_CONFIG', payload: { index, configChanges } });

  const handleRemove = async () => {
    if (await ask({ title: 'Delete workflow step?', message: `Are you sure you want to delete this step: "${step.name}"?`, action: 'Delete step', destructive: true }) !== null) {
      dispatch({ type: 'REMOVE_STEP', payload: index });
    }
  };

  return (
    <div className="workflow-step-card" style={{ margin: '0.5rem 0' }}>
      {dialog}
      <div className="workflow-step-header">
        <span className="btn-icon workflow-step-drag-handle" {...dragHandleProps} title="Reorder step" aria-label={`Reorder step ${index + 1}`}>&#x2630;</span>
        <span className="workflow-step-index" style={{ fontWeight: 700 }}>#{index + 1}</span>
        <input type="text" value={step.name} onChange={e => update({ name: e.target.value })} placeholder="Step name" style={{ flex: 1 }} />
        <label className="checkbox-label" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={step.continueOnError} onChange={e => update({ continueOnError: e.target.checked })} /> Continue on error
        </label>
        <button className="btn-icon" title="Remove" onClick={handleRemove}>&#x2715;</button>
      </div>
      <StepConfigEditor stepType={step.stepType} config={step.config} onChange={updateConfig} allSteps={state.steps} currentStepId={step.stepId}
        workflowIntegrationId={workflowIntegrationId} sourceType={notificationSource ? 'Notification' : 'Alert'} workflowSteps={state.steps} currentStepOrder={step.stepOrder} />
    </div>
  );
};

function getDefaultConfig(stepType: string): Record<string, any> {
  switch (stepType) {
    case 'Notification': return { targets: [], channelTypes: ['Email'], adHocEmails: '', excludeChannelEmailRecipients: true };
    case 'Delay': return { delayMinutes: 5 };
    case 'Webhook': return { url: '', method: 'POST', bodyTemplate: '{}', headers: {}, responseMappingsJson: '{}', timeoutSeconds: 30, retryCount: 0, retryDelaySeconds: 5 };
    case 'IntegrationAction': return { integrationId: '', integrationActionId: '', actionKey: 'createTicket', method: 'POST', pathTemplate: '', bodyTemplate: '{}', headers: {}, ticketIdVariable: '', fieldMappings: {}, retryCount: 0, retryDelaySeconds: 5 };
    case 'DispatchOutboundDelivery': return { integrationId: '', integrationActionId: '', fieldMappings: {}, timeoutSeconds: 60 };
    case 'UpdateAlert': return { status: '', priority: '', assignTo: '', assignTargetType: '', assignTargetId: '' };
    case 'Ping': return { target: '', secretRef: '', packetCount: 4, timeoutMs: 1000, onSuccessStepId: null, onFailureStepId: null, onSuccessTarget: '', onFailureTarget: '' };
    case 'Escalate': return { action: 'start' };
    case 'Condition': return { onTrueStepId: null, onFalseStepId: null, onTrueTarget: '', onFalseTarget: '', field: 'alert.severity', operator: 'equals', value: 'Critical' };
    case 'RunSubWorkflow': return { subWorkflowId: '', alias: '', inputMappings: {} };
    case 'ReturnResult': return { resultCode: '', outputMappings: {} };
    case 'EndWorkflow': return {};
    default: return {};
  }
}

const WorkflowStringMappingsEditor: React.FC<{
  label: string;
  nameLabel: string;
  valueLabel: string;
  mappings: Record<string, string>;
  onChange: (mappings: Record<string, string>) => void;
}> = ({ label, nameLabel, valueLabel, mappings, onChange }) => {
  const entries = Object.entries(mappings || {});
  const updateEntry = (index: number, field: 'name' | 'value', value: string) => {
    const next = [...entries];
    const [oldName, oldValue] = next[index];
    const name = field === 'name' ? value : oldName;
    const source = field === 'value' ? value : oldValue;
    next[index] = [name, source];
    onChange(Object.fromEntries(next));
  };
  return <div className="workflow-string-mappings">
    <div className="workflow-string-mappings-header"><strong>{label}</strong>
      <button type="button" className="btn-secondary btn-sm" onClick={() => onChange({ ...mappings, [`value${entries.length + 1}`]: '' })}>
        <Plus size={14} /> Add mapping
      </button>
    </div>
    {entries.map(([name, value], index) => <div className="workflow-string-mapping-row" key={`${index}:${name}`}>
      <label>{nameLabel}<input value={name} onChange={event => updateEntry(index, 'name', event.target.value)} /></label>
      <label>{valueLabel}<input value={value} onChange={event => updateEntry(index, 'value', event.target.value)} placeholder="alert.title" /></label>
      <button type="button" className="btn-icon" aria-label={`Remove ${nameLabel.toLowerCase()} mapping ${index + 1}`} title="Remove mapping"
        onClick={() => onChange(Object.fromEntries(entries.filter((_, entryIndex) => entryIndex !== index)))}><Trash2 size={14} /></button>
    </div>)}
    {!entries.length && <div className="help-text">No mappings. Values remain unchanged.</div>}
  </div>;
};

const BranchExitEditor: React.FC<{
  label: string;
  target: string;
  resultCode: string;
  isSubWorkflow: boolean;
  declaredResultCodes: string[];
  onChange: (target: string, resultCode: string) => void;
}> = ({ label, target, resultCode, isSubWorkflow, declaredResultCodes, onChange }) => (
  <div className="form-group">
    <label>{label}</label>
    <select value={target || ''} onChange={event => onChange(event.target.value, '')}>
      <option value="">Continue or jump to selected step</option>
      <option value="EndWorkflow">End workflow</option>
      {isSubWorkflow && <option value="ReturnResult">Return result</option>}
    </select>
    {target === 'ReturnResult' && <label>Result code
      <select value={resultCode || ''} onChange={event => onChange(target, event.target.value)}>
        <option value="">Select a declared result code</option>
        {declaredResultCodes.map(code => <option key={code} value={code}>{code}</option>)}
      </select>
    </label>}
  </div>
);

const StepConfigEditor: React.FC<{
  stepType: string;
  config: Record<string, any>;
  onChange: (cfg: Record<string, any>) => void;
  allSteps: WorkflowStepDto[];
  currentStepId?: string;
  workflowIntegrationId?: string | null;
  sourceType: ContentSource;
  workflowSteps: WorkflowStepDto[];
  currentStepOrder: number;
}> = ({ stepType, config, onChange, allSteps, currentStepId, workflowIntegrationId, sourceType, workflowSteps, currentStepOrder }) => {
  const {
    state,
    workflowId,
    globalVariableNames,
    notificationSource,
    isSubWorkflow,
    declaredResultCodes,
    availableSubWorkflows,
    refreshBuilderData,
    channels,
    channelSenders,
    users,
    groups,
    schedules,
    outboundIntegrations,
    outboundIntegrationActionsByIntegration,
    escalationPolicies,
    conditionMetadata,
  } = useContext(StepsBuilderContext)!;
  const workflowStepsWithResponseMappings = workflowSteps.map(step => {
    if (step.stepType !== 'IntegrationAction') return step;
    const stepConfig = (step.config || {}) as Record<string, unknown>;
    const integrationId = String(stepConfig.integrationId || '');
    const actionId = String(stepConfig.integrationActionId || '');
    const action = outboundIntegrationActionsByIntegration[integrationId]?.find(item => item.outboundIntegrationActionId === actionId);
    return {
      ...step,
      config: {
        ...stepConfig,
        responseMappingsJson: action?.responseMappingsJson || stepConfig.responseMappingsJson,
      },
    };
  });
  const [webhookTestVariablesInput, setWebhookTestVariablesInput] = useState<string>('{}');
  const [webhookTestRunning, setWebhookTestRunning] = useState(false);
  const [webhookTestError, setWebhookTestError] = useState<string | null>(null);
  const [webhookTestResult, setWebhookTestResult] = useState<any | null>(null);
  const [webhookVariableTarget, setWebhookVariableTarget] = useState<'url' | 'body' | 'header'>('body');
  const [webhookHeaderTarget, setWebhookHeaderTarget] = useState('');
  const [integrationMappingTarget, setIntegrationMappingTarget] = useState('');
  const [subWorkflowInputTarget, setSubWorkflowInputTarget] = useState('');
  const webhookUrlRef = useRef<HTMLInputElement | null>(null);
  const webhookBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [promotionRunning, setPromotionRunning] = useState(false);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [promotionResult, setPromotionResult] = useState<PromoteWorkflowWebhookStepResponse | null>(null);
  const [promotionIntegrationName, setPromotionIntegrationName] = useState<string>('');
  const [promotionProvider, setPromotionProvider] = useState<string>('CustomWebhook');
  const [promotionActionName, setPromotionActionName] = useState<string>('');
  const [rebindRunning, setRebindRunning] = useState(false);
  const [rebindError, setRebindError] = useState<string | null>(null);
  const [rebindResult, setRebindResult] = useState<RebindWorkflowIntegrationStepResponse | null>(null);
  const [rebindVersion, setRebindVersion] = useState<string>(config.integrationVersion || 'v1');
  const [showBindingActions, setShowBindingActions] = useState(false);
  const baseConditionFieldOptions = Array.isArray(conditionMetadata.fields) && conditionMetadata.fields.length > 0
    ? conditionMetadata.fields.map((f) => ({ value: f.key, label: f.label, category: f.category }))
    : DEFAULT_CONDITION_FIELD_OPTIONS;
  const subWorkflowConditionFields = workflowSteps.filter(step => step.stepType === 'RunSubWorkflow' && step.config?.alias)
    .flatMap(step => {
      const prefix = `subworkflow.${String(step.config.alias).trim()}`;
      const options = [
        { value: `${prefix}.status`, label: `${step.config.alias} status`, category: 'Sub-workflows' },
        { value: `${prefix}.resultCode`, label: `${step.config.alias} result code`, category: 'Sub-workflows' },
      ];
      const definition = availableSubWorkflows.find(item => item.workflowId === step.config?.subWorkflowId);
      if (definition?.resultCodes.length) options[1].label += ` (${definition.resultCodes.join(', ')})`;
      return options;
    });
  const globalVariableConditionFields = globalVariableNames.map(name => ({
    value: `global.${name}`, label: `Global: ${name}`, category: 'Workflow Globals'
  }));
  const conditionFieldOptions = [...baseConditionFieldOptions, ...subWorkflowConditionFields, ...globalVariableConditionFields];
  const conditionOperators = Array.isArray(conditionMetadata.operators) && conditionMetadata.operators.length > 0
    ? conditionMetadata.operators.map((o) => ({ value: o.key, label: o.label, hint: o.hint }))
    : DEFAULT_CONDITION_OPERATORS;
  const stepOptions = allSteps.map(s => ({ label: `${s.stepOrder + 1}: ${s.name}`, value: s.stepId }));
  const filteredStepOptions = stepOptions.filter(o => o.value !== currentStepId);
  const selectedOperator = conditionOperators.find(o => o.value === (config.operator || 'equals'));
  const selectedFieldOption = conditionFieldOptions.find(f => f.value === (config.field || ''));
  const selectedFieldValue = selectedFieldOption ? selectedFieldOption.value : CONDITION_CUSTOM_FIELD_VALUE;
  const trimmedRebindVersion = (rebindVersion || '').trim();
  const hasExplicitRebindVersion = trimmedRebindVersion.length > 0;
  const rebindVersionIsValid = !hasExplicitRebindVersion || INTEGRATION_VERSION_PATTERN.test(trimmedRebindVersion);

  useEffect(() => {
    if (stepType !== 'IntegrationAction') {
      return;
    }

    setRebindVersion(config.integrationVersion || 'v1');
    setRebindError(null);
    setRebindResult(null);
    setShowBindingActions(false);
  }, [stepType, currentStepId, config.integrationVersion]);

  const runWebhookLiveTest = async () => {
    if (state.isDirty) {
      setWebhookTestError('Save workflow step changes before live test so server and editor state stay in sync.');
      setWebhookTestResult(null);
      return;
    }

    setWebhookTestRunning(true);
    setWebhookTestError(null);
    setWebhookTestResult(null);

    try {
      let variables: Record<string, string> = {};
      const raw = webhookTestVariablesInput.trim();
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          variables = Object.fromEntries(
            Object.entries(parsed as Record<string, unknown>).map(([k, v]) => [k, v == null ? '' : String(v)]),
          );
        } else {
          throw new Error('Variables must be a JSON object map.');
        }
      }

      const result = await tenantApi.testWorkflowWebhook({
        config: {
          url: config.url || '',
          method: config.method || 'POST',
          headers: config.headers || {},
          bodyTemplate: config.bodyTemplate || '',
          timeoutSeconds: config.timeoutSeconds || 30,
          retryCount: config.retryCount ?? 0,
          retryDelaySeconds: config.retryDelaySeconds ?? 5,
          integrationId: config.integrationId || '',
        },
        variables,
      });
      setWebhookTestResult(result);
    } catch (err: any) {
      setWebhookTestError(err.message || 'Webhook live test failed');
    } finally {
      setWebhookTestRunning(false);
    }
  };

  const promoteWebhookToOutbound = async () => {
    if (!workflowId || !currentStepId) {
      setPromotionError('Workflow/step context is missing for promotion.');
      return;
    }

    const integrationName = promotionIntegrationName.trim();
    if (!integrationName) {
      setPromotionError('Integration name is required for promotion.');
      return;
    }

    if (state.isDirty) {
      setPromotionError('Save workflow step changes before promotion so server and editor state are in sync.');
      return;
    }

    setPromotionRunning(true);
    setPromotionError(null);
    setPromotionResult(null);

    try {
      const result = await tenantApi.promoteWorkflowWebhookStep(workflowId, currentStepId, {
        integrationName,
        provider: (promotionProvider || 'CustomWebhook').trim(),
        actionName: promotionActionName.trim() || undefined,
      });

      await refreshBuilderData();
      setPromotionResult(result);
    } catch (err: any) {
      setPromotionError(err?.message || 'Failed to promote webhook step');
    } finally {
      setPromotionRunning(false);
    }
  };

  const rebindIntegrationActionStep = async () => {
    if (!workflowId || !currentStepId) {
      setRebindError('Workflow/step context is missing for rebind.');
      return;
    }

    const integrationId = (config.integrationId || '').trim();
    const integrationActionId = (config.integrationActionId || '').trim();
    if (!integrationId || !integrationActionId) {
      setRebindError('Select both integration and action before rebinding.');
      return;
    }

    if (state.isDirty) {
      setRebindError('Save workflow step changes before rebind so server and editor state are in sync.');
      return;
    }

    if (hasExplicitRebindVersion && !rebindVersionIsValid) {
      setRebindError('Pinned version must match [A-Za-z0-9][A-Za-z0-9._-]{0,63} (for example: v1, v1.1, 2026-06).');
      return;
    }

    setRebindRunning(true);
    setRebindError(null);
    setRebindResult(null);

    try {
      const result = await tenantApi.rebindWorkflowIntegrationStep(workflowId, currentStepId, {
        integrationId,
        integrationActionId,
        integrationVersion: (rebindVersion || '').trim() || undefined,
      });

      await refreshBuilderData();
      setRebindResult(result);
    } catch (err: any) {
      setRebindError(err?.message || 'Failed to rebind integration action step');
    } finally {
      setRebindRunning(false);
    }
  };

  switch (stepType) {
    case 'Notification':
    {
      const targets = Array.isArray(config.targets) ? config.targets : [];
      const channelTypes = Array.isArray(config.channelTypes) ? config.channelTypes : [];
      const overrides = notificationOverrideState(config);

      const addTarget = () => {
        onChange({
          targets: [...targets, { type: 'Channel', id: '' }],
        });
      };

      const updateTarget = (index: number, next: Record<string, any>) => {
        const nextTargets = targets.map((t, i) => (i === index ? { ...t, ...next } : t));
        onChange({ targets: nextTargets });
      };

      const removeTarget = (index: number) => {
        onChange({ targets: targets.filter((_: unknown, i: number) => i !== index) });
      };

      const toggleChannelType = (channelType: string) => {
        const has = channelTypes.includes(channelType);
        const next = has
          ? channelTypes.filter((c: string) => c !== channelType)
          : [...channelTypes, channelType];
        onChange({ channelTypes: next });
      };

      const resolveTargetOptions = (targetType: string) => {
        if (targetType === 'Channel') {
          return (Array.isArray(channels) ? channels : [])
            .filter(c => c.isEnabled)
            .map(c => ({ value: c.channelId, label: notificationChannelTargetLabel(c, channelSenders) }));
        }

        if (targetType === 'User') {
          return (Array.isArray(users) ? users : [])
            .map(u => ({ value: u.userId, label: u.displayName || u.email || u.userId }));
        }

        if (targetType === 'Group') {
          return (Array.isArray(groups) ? groups : [])
            .map(g => ({ value: g.groupId, label: g.name }));
        }

        if (targetType === 'OnCall') {
          return (Array.isArray(schedules) ? schedules : [])
            .map(s => ({ value: s.scheduleId, label: s.name }));
        }

        return [];
      };

      return (
        <>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '0.8rem' }}>Assign to/Send via</label>
            {targets.length === 0 && (
              <div className="help-text" style={{ marginBottom: 8 }}>No targets selected yet.</div>
            )}
            {targets.map((target: any, idx: number) => {
              const targetType = target?.type || 'Channel';
              const options = resolveTargetOptions(targetType);
              return (
                <div key={`wf-target-${idx}`} className="workflow-notification-target">
                  <select
                    value={targetType}
                    onChange={e => updateTarget(idx, { type: e.target.value, id: '' })}
                  >
                    {NOTIFICATION_TARGET_TYPES.map(type => <option key={type} value={type}>{type === 'Channel' ? 'Notification Channel' : type === 'OnCall' ? 'On-Call Schedule' : type}</option>)}
                  </select>
                  {targetType === 'OnCall' ? (
                    <select
                      value={target?.id || ''}
                      onChange={e => updateTarget(idx, { id: e.target.value })}
                    >
                      <option value="">-- Select Schedule --</option>
                      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <select
                      value={target?.id || ''}
                      onChange={e => updateTarget(idx, { id: e.target.value })}
                    >
                      <option value="">-- Select {targetType === 'Channel' ? 'Notification Channel' : targetType} --</option>
                      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                  <button type="button" className="btn-secondary btn-sm" onClick={() => removeTarget(idx)}>Remove</button>
                </div>
              );
            })}
          </div>

          <WorkflowNotificationContent config={config} onChange={onChange} workflowId={workflowId} workflowIntegrationId={workflowIntegrationId}
            recipientAction={<button type="button" className="btn-secondary btn-sm" onClick={addTarget}>+ Add Recipient</button>}
            sourceType={sourceType} workflowSteps={workflowStepsWithResponseMappings} currentStepOrder={currentStepOrder}
            globalVariableNames={globalVariableNames} />

          {config.channelId && <div className="help-text">Notification Channel (legacy): {(() => {
            const channel = channels.find(item => item.channelId === config.channelId);
            return channel ? notificationChannelTargetLabel(channel, channelSenders) : config.channelId;
          })()}</div>}
          {overrides.deliveryEnabled && <details className="workflow-notification-overrides" open={overrides.expanded || undefined}>
            <summary>Delivery Overrides</summary>
          <fieldset>
            <legend style={{ fontSize: '0.8rem' }}>Delivery Channel Types</legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {NOTIFICATION_CHANNEL_TYPES.map(channelType => (
                <label key={channelType} className="checkbox-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.35rem 0.55rem' }}>
                  <input
                    type="checkbox"
                    checked={channelTypes.includes(channelType)}
                    onChange={() => toggleChannelType(channelType)}
                  />
                  {channelType}
                </label>
              ))}
            </div>
            <div className="help-text" style={{ marginTop: 6 }}>
              User, Group and additional email recipients default to Email. Select a delivery type for On-Call recipients. Notification Channels always use their configured transport.
            </div>
          </fieldset>

          </details>}
        </>
      );
    }
    case 'Delay':
      return (
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: '0.8rem' }}>Delay (minutes)</label>
          <input type="number" min={1} value={config.delayMinutes || 5} onChange={e => onChange({ delayMinutes: parseInt(e.target.value) || 1 })} style={{ width: 120 }}/>
        </div>
      );
    case 'Escalate':
      return (
        <>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ fontSize: '0.8rem' }}>Action</label>
            <select value={config.action || 'start'} onChange={e => onChange({ action: e.target.value })}>
              <option value="start">Start Escalation</option>
              <option value="stop">Stop Escalation</option>
            </select>
          </div>
          {(config.action || 'start') === 'start' && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.8rem' }}>Escalation Policy</label>
              <select
                value={config.policyId || ''}
                onChange={e => onChange({ policyId: e.target.value || null })}
              >
                <option value="">Auto-resolve by alert priority/default</option>
                {escalationPolicies.map(p => (
                  <option key={p.policyId} value={p.policyId}>
                    {p.name}
                    {p.priority ? ` (Priority ${p.priority})` : p.isDefault ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
              {escalationPolicies.length === 0 && (
                <div className="help-text">No escalation policies found. Create one in Escalation Policies first.</div>
              )}
            </div>
          )}
        </>
      );
    case 'Ping':
      return (
        <>
          <div className="form-row">
            <div className="form-group">
              <label>Target (IP or DNS)</label>
              <input
                value={config.target || ''}
                onChange={e => onChange({ target: e.target.value })}
                placeholder="8.8.8.8 or host.example.com"
              />
            </div>
            <div className="form-group">
              <label>Secret Ref (optional)</label>
              <input
                value={config.secretRef || ''}
                onChange={e => onChange({ secretRef: e.target.value })}
                placeholder="my_ping_target"
              />
            </div>
            <div className="form-group">
              <label>Packet Count</label>
              <input
                type="number"
                min={1}
                max={20}
                value={config.packetCount || 4}
                onChange={e => onChange({ packetCount: Math.max(1, Math.min(20, Number(e.target.value) || 4)) })}
              />
            </div>
            <div className="form-group">
              <label>Timeout (ms)</label>
              <input
                type="number"
                min={250}
                max={10000}
                step={250}
                value={config.timeoutMs || 1000}
                onChange={e => onChange({ timeoutMs: Math.max(250, Math.min(10000, Number(e.target.value) || 1000)) })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>On Success -&gt; Jump To</label>
              <select disabled={!!config.onSuccessTarget} value={config.onSuccessStepId || ''}
                onChange={e => onChange({ onSuccessStepId: e.target.value || null, onSuccessTarget: '' })}>
                <option value="">(Continue to next step)</option>
                {filteredStepOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>On Failure -&gt; Jump To</label>
              <select disabled={!!config.onFailureTarget} value={config.onFailureStepId || ''}
                onChange={e => onChange({ onFailureStepId: e.target.value || null, onFailureTarget: '' })}>
                <option value="">(Continue to next step)</option>
                {filteredStepOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <BranchExitEditor label="On success exit" target={config.onSuccessTarget} resultCode={config.onSuccessResultCode}
              isSubWorkflow={isSubWorkflow} declaredResultCodes={declaredResultCodes}
              onChange={(target, resultCode) => onChange({ onSuccessTarget: target, onSuccessResultCode: resultCode,
                ...(target ? { onSuccessStepId: null, onSuccessStepOrder: null } : {}) })} />
            <BranchExitEditor label="On failure exit" target={config.onFailureTarget} resultCode={config.onFailureResultCode}
              isSubWorkflow={isSubWorkflow} declaredResultCodes={declaredResultCodes}
              onChange={(target, resultCode) => onChange({ onFailureTarget: target, onFailureResultCode: resultCode,
                ...(target ? { onFailureStepId: null, onFailureStepOrder: null } : {}) })} />
          </div>
          <div className="help-text" style={{ marginTop: 6 }}>
            Executes multiple probes and records reply/timeout output variables for later conditions. Packet count default is 4 and maximum is 20. Secret Ref resolves via <code>{'{{secret.name}}'}</code> when target is blank.
          </div>
        </>
      );
    case 'RunSubWorkflow':
    {
      const compatibleSubWorkflows = availableSubWorkflows.filter(definition =>
        definition.workflowId !== workflowId && definition.isEnabled &&
        (definition.sourceCompatibility === 'Either' || definition.sourceCompatibility === sourceType));
      const inputMappings = (config.inputMappings || {}) as Record<string, string>;
      const inputTargets = Object.keys(inputMappings);
      const inputTarget = inputTargets.includes(subWorkflowInputTarget) ? subWorkflowInputTarget : inputTargets[0] || '';
      return <>
        <div className="form-row">
          <div className="form-group">
            <label>Sub-workflow</label>
            <select value={config.subWorkflowId || ''} onChange={event => onChange({ subWorkflowId: event.target.value })}>
              <option value="">Select a compatible sub-workflow</option>
              {compatibleSubWorkflows.map(definition => <option key={definition.workflowId} value={definition.workflowId}>
                {definition.name} ({definition.scope})
              </option>)}
            </select>
            {!compatibleSubWorkflows.length && <div className="help-text">No enabled compatible shared sub-workflows are available.</div>}
          </div>
          <div className="form-group">
            <label>Result alias</label>
            <input value={config.alias || ''} onChange={event => onChange({ alias: event.target.value })} placeholder="ticket" />
          </div>
        </div>
        <WorkflowStringMappingsEditor label="Input mappings" nameLabel="Sub-workflow input" valueLabel="Parent variable"
          mappings={inputMappings}
          onChange={inputMappings => onChange({ inputMappings })} />
        <p className="help-text">Workflow globals are inherited through the execution scope and are available inside the sub-workflow. Input mappings are persisted on the child instance and cannot carry global values.</p>
        {inputTargets.length > 0 && <>
          <label>Parent input to map<select aria-label="Sub-workflow input mapping target" value={inputTarget}
            onChange={event => setSubWorkflowInputTarget(event.target.value)}>
            {inputTargets.map(name => <option key={name} value={name}>{name}</option>)}
          </select></label>
          <WorkflowVariableBrowser workflowIntegrationId={workflowIntegrationId} sourceType={sourceType}
            workflowSteps={workflowStepsWithResponseMappings} currentStepOrder={currentStepOrder}
            targetLabel="Sub-workflow parent input"
            onSelect={path => inputTarget && onChange({ inputMappings: { ...inputMappings, [inputTarget]: path } })} />
        </>}
      </>;
    }
    case 'ReturnResult':
      return <>
        <div className="form-group">
          <label>Result code</label>
          <select value={config.resultCode || ''} onChange={event => onChange({ resultCode: event.target.value })}>
            <option value="">Select a declared result code</option>
            {declaredResultCodes.map(code => <option key={code} value={code}>{code}</option>)}
          </select>
          {!isSubWorkflow && <div className="error-message" role="alert">Return result is only available inside a sub-workflow.</div>}
          {isSubWorkflow && !declaredResultCodes.length && <div className="help-text">Declare result codes in sub-workflow details before adding Return result.</div>}
        </div>
        <WorkflowStringMappingsEditor label="Returned outputs" nameLabel="Output name" valueLabel="Workflow variable"
          mappings={(config.outputMappings || {}) as Record<string, string>}
          onChange={outputMappings => onChange({ outputMappings })} />
      </>;
    case 'EndWorkflow':
      return <div className="help-text">Ends the current workflow successfully. A sub-workflow returns Completed without a business result code.</div>;
    case 'Condition': {
      const isCurrentAlert = config.evaluationSource === 'CurrentAlert';
      const currentAlertField = CURRENT_ALERT_CONDITION_FIELDS.find(f => f.value === (config.field || '').trim().toLowerCase())?.value || 'alert.status';
      const valueless = VALUELESS_CONDITION_OPERATORS.has((config.operator || '').toLowerCase());
      return (
        <>
          <div className="form-group">
            <label>Evaluate against
              <select aria-label="Evaluate against" value={config.evaluationSource || 'WorkflowSnapshot'}
                onChange={event => onChange({
                  evaluationSource: event.target.value,
                  ...(event.target.value === 'CurrentAlert'
                    ? { field: CURRENT_ALERT_CONDITION_FIELDS.find(f => f.value === (config.field || '').trim().toLowerCase())?.value || 'alert.status' }
                    : {}),
                })}>
                <option value="WorkflowSnapshot">Workflow snapshot</option>
                {sourceType === 'Alert' && <option value="CurrentAlert">Current alert</option>}
                {sourceType !== 'Alert' && config.evaluationSource === 'CurrentAlert' &&
                  <option value="CurrentAlert" disabled>Current alert (requires an alert source)</option>}
              </select>
            </label>
            <div className="help-text">{isCurrentAlert
              ? (currentAlertField === 'alert.externalticketid'
                ? 'Reads the alert\'s external ticket ID live when this step runs (alert record, then the ticket association saved by Create Ticket). Use "Is empty" to skip close/update actions when no ticket was ever created. Lookup errors stop the workflow, even with Continue on error enabled.'
                : 'Reads the current alert status when this step runs. Lookup errors stop the workflow, even with Continue on error enabled. Original payload and trigger variables remain unchanged.')
              : 'Uses workflow variables without refreshing the alert from the database.'}</div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Common Field</label>
              {isCurrentAlert ? (
                <select aria-label="Current alert field" value={currentAlertField}
                  onChange={e => onChange({ field: e.target.value })}>
                  {CURRENT_ALERT_CONDITION_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              ) : (
              <select
                value={selectedFieldValue}
                onChange={e => {
                  const nextValue = e.target.value;
                  if (nextValue !== CONDITION_CUSTOM_FIELD_VALUE) {
                    onChange({ field: nextValue });
                  }
                }}
              >
                {conditionFieldOptions.map((f) => (
                  <option key={f.value} value={f.value}>{`[${f.category}] ${f.label}`}</option>
                ))}
                <option value={CONDITION_CUSTOM_FIELD_VALUE}>Custom field...</option>
              </select>
              )}
            </div>
            <div className="form-group">
              <label>Field Key</label>
              <input
                type="text"
                disabled={config.evaluationSource === 'CurrentAlert'}
                value={config.field || ''}
                onChange={e => onChange({ field: e.target.value })}
                placeholder="alert.severity"
              />
              <div className="help-text" style={{ marginTop: '0.35rem' }}>
                Choose a common alert field or type a custom variable (for example: <code>{conditionMetadata.customFieldPrefix}region</code>).
              </div>
              {conditionMetadata.notes && (
                <div className="help-text" style={{ marginTop: '0.35rem' }}>{conditionMetadata.notes}</div>
              )}
              <div className="help-text" style={{ marginTop: '0.35rem' }}>
                Suggested fields:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {conditionFieldOptions.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={config.evaluationSource === 'CurrentAlert'}
                    style={{ padding: '0.2rem 0.45rem' }}
                    title={`${f.label} (${f.category})`}
                    onClick={() => onChange({ field: f.value })}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group"><label>Operator</label>
              <select value={config.operator || 'equals'} onChange={e => onChange({ operator: e.target.value })}>
                {conditionOperators.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
              </select>
              <div className="help-text" style={{ marginTop: '0.35rem' }}>
                {selectedOperator?.hint}
              </div>
            </div>
            <div className="form-group"><label>Value</label><input type="text" aria-label="Condition value" disabled={valueless}
              placeholder={valueless ? 'Not used by this operator' : undefined}
              value={valueless ? '' : config.value || ''} onChange={e => onChange({ value: e.target.value })}/></div>
          </div>
          <div className="form-row">
            {(['True', 'False'] as const).map(branch => {
              const prefix = `on${branch}`;
              const target = config[`${prefix}Target`];
              const stepId = config[`${prefix}StepId`] ||
                allSteps.find(step => step.stepOrder === config[`${prefix}StepOrder`])?.stepId || '';
              const selected = target || (stepId ? `step:${stepId}` :
                config[`${prefix}StepOrder`] != null ? `order:${config[`${prefix}StepOrder`]}` : '');
              const unavailable = selected && !['EndWorkflow', ...(isSubWorkflow ? ['ReturnResult'] : [])].includes(selected)
                && !filteredStepOptions.some(option => `step:${option.value}` === selected);
              return <div className="form-group" key={branch}>
                <label>If {branch}
                  <select aria-label={`If ${branch}`} value={selected}
                    onChange={event => {
                      const value = event.target.value;
                      onChange({
                        [`${prefix}Target`]: value === 'EndWorkflow' || value === 'ReturnResult' ? value : '',
                        [`${prefix}StepId`]: value.startsWith('step:') ? value.slice(5) : null,
                        [`${prefix}StepOrder`]: null,
                        [`${prefix}ResultCode`]: null,
                      });
                    }}>
                    <option value="">Next Step</option>
                    {filteredStepOptions.map(option => <option key={option.value} value={`step:${option.value}`}>Jump to {option.label}</option>)}
                    <option value="EndWorkflow">End workflow</option>
                    {isSubWorkflow && <option value="ReturnResult">Return result</option>}
                    {unavailable && <option value={selected}>Unavailable saved destination ({selected})</option>}
                  </select>
                </label>
                {target === 'ReturnResult' && <label>Result code
                  <select aria-label={`If ${branch} result code`} value={config[`${prefix}ResultCode`] || ''}
                    onChange={event => onChange({ [`${prefix}ResultCode`]: event.target.value })}>
                    <option value="">Select a declared result code</option>
                    {declaredResultCodes.map(code => <option key={code} value={code}>{code}</option>)}
                  </select>
                </label>}
              </div>;
            })}
          </div>
        </>
      );
    }
    // Other step types would be added here
    case 'Webhook':
    {
      const webhookUrl = (config.url || '').trim();
      const draftStateLabel = state.isDirty ? 'Unsaved changes' : 'All changes saved';
      const draftStateColor = state.isDirty ? 'var(--color-severity-medium)' : 'var(--color-severity-low)';
      const rawWebhookVariables = webhookTestVariablesInput.trim();
      let webhookVariablesParseError: string | null = null;
      if (rawWebhookVariables) {
        try {
          const parsed = JSON.parse(rawWebhookVariables);
          if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            webhookVariablesParseError = 'Variables must be a JSON object map.';
          }
        } catch {
          webhookVariablesParseError = 'Variables JSON is invalid.';
        }
      }

      const liveTestPrecheckMessages: string[] = [];
      if (!webhookUrl) {
        liveTestPrecheckMessages.push('Set a Webhook/API URL before running live test.');
      }
      if (webhookVariablesParseError) {
        liveTestPrecheckMessages.push(webhookVariablesParseError);
      }
      if (state.isDirty) {
        liveTestPrecheckMessages.push('Save workflow step changes before running live test.');
      }

      const canRunWebhookLiveTest =
        !webhookTestRunning &&
        !!webhookUrl &&
        !webhookVariablesParseError &&
        !state.isDirty;

      const normalizedProvider = (promotionProvider || '').trim() || 'CustomWebhook';
      const integrationName = promotionIntegrationName.trim();
      const actionName = promotionActionName.trim() || 'Create Ticket';

      const promotionPrecheckMessages: string[] = [];
      if (isSubWorkflow) {
        promotionPrecheckMessages.push('Promotion is disabled for sub-workflows until the API supports revision-guarded promotion.');
      }
      if (!currentStepId) {
        promotionPrecheckMessages.push('Step context is missing. Save and reopen the step builder.');
      }
      if (!webhookUrl) {
        promotionPrecheckMessages.push('Set a Webhook/API URL before promotion.');
      }
      if (!integrationName) {
        promotionPrecheckMessages.push('Integration name is required.');
      }
      if (state.isDirty) {
        promotionPrecheckMessages.push('Save workflow step changes before promotion to keep server/editor state in sync.');
      }

      const canAttemptPromotion =
        !promotionRunning &&
        !isSubWorkflow &&
        !!currentStepId &&
        !!webhookUrl &&
        !!integrationName &&
        !state.isDirty;

      const promotionEligibilityState = canAttemptPromotion ? 'Eligible' : 'Not eligible';
      const promotionEligibilityColor = canAttemptPromotion ? 'var(--color-severity-low)' : 'var(--color-severity-medium)';

      const promotionSummaryText = promotionResult
        ? `Promoted to ${promotionResult.integrationName}${promotionResult.integrationVersion ? ` @ ${promotionResult.integrationVersion}` : ''} (${promotionResult.actionName}).`
        : '';

      const clearPromotionState = () => {
        setPromotionError(null);
        setPromotionResult(null);
      };

      const insertWebhookVariable = (path: string) => {
        const token = `{{${path}}}`;
        if (webhookVariableTarget === 'header') {
          const headerName = webhookHeaderTarget.trim();
          if (!headerName || /authorization|api[-_]?key|token|secret|credential/i.test(headerName)) return;
          const headers = { ...(config.headers || {}) };
          const current = String(headers[headerName] || '');
          headers[headerName] = `${current}${current && !/\s$/.test(current) ? ' ' : ''}${token}`;
          onChange({ headers });
          return;
        }

        const isUrl = webhookVariableTarget === 'url';
        const ref = isUrl ? webhookUrlRef.current : webhookBodyRef.current;
        const key = isUrl ? 'url' : 'bodyTemplate';
        const current = String(config[key] || '');
        const start = ref?.selectionStart ?? current.length;
        const end = ref?.selectionEnd ?? start;
        const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
        const caret = start + token.length;
        onChange({ [key]: next });
        requestAnimationFrame(() => {
          const target = isUrl ? webhookUrlRef.current : webhookBodyRef.current;
          target?.focus();
          target?.setSelectionRange(caret, caret);
        });
      };

      return (
        <>
          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}><label>URL</label><input ref={webhookUrlRef} type="text" value={config.url || ''} onChange={e => onChange({ url: e.target.value })} /></div>
            <div className="form-group" style={{ flex: 0.5 }}><label>Method</label>
              <select value={config.method || 'POST'} onChange={e => onChange({ method: e.target.value })}>
                <option>GET</option><option>POST</option><option>PUT</option><option>PATCH</option><option>DELETE</option><option>HEAD</option><option>OPTIONS</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 0.5 }}>
              <label>Timeout (seconds)</label>
              <input
                type="number"
                min={1}
                max={300}
                value={config.timeoutSeconds || 30}
                onChange={e => onChange({ timeoutSeconds: Math.max(1, Math.min(300, parseInt(e.target.value) || 30)) })}
              />
            </div>
            <div className="form-group" style={{ flex: 0.4 }}>
              <label>Retries</label>
              <input
                aria-label="Webhook retry count"
                type="number"
                min={0}
                max={10}
                value={config.retryCount ?? 0}
                onChange={e => onChange({ retryCount: Math.max(0, Math.min(10, parseInt(e.target.value) || 0)) })}
              />
            </div>
            <div className="form-group" style={{ flex: 0.5 }}>
              <label>Retry Delay (seconds)</label>
              <input
                aria-label="Webhook retry delay"
                type="number"
                min={1}
                max={300}
                value={config.retryDelaySeconds ?? 5}
                onChange={e => onChange({ retryDelaySeconds: Math.max(1, Math.min(300, parseInt(e.target.value) || 5)) })}
              />
            </div>
          </div>
          <div className="form-group"><label>Body Template (JSON)</label><textarea ref={webhookBodyRef} value={config.bodyTemplate || ''} onChange={e => onChange({ bodyTemplate: e.target.value })} rows={3} /></div>
          <div className="form-group"><label>Response Mappings JSON (optional)</label>
            <textarea aria-label="Webhook response mappings JSON" value={config.responseMappingsJson || '{}'}
              onChange={event => onChange({ responseMappingsJson: event.target.value })} rows={5}
              placeholder={'{\n  "alert.data.ticketId": "$.ticket.id",\n  "artifact.image.capture": "$.image"\n}'} />
            <p className="help-text" style={{ marginTop: 6 }}>Map JSON Pointer or JSONPath response sources to workflow variables; use artifact.image/json/text/link/field targets to store bounded values with an Alert.</p>
          </div>
          <div className="form-group"><label>Headers</label><KeyValueEditor value={config.headers || {}} onChange={h => onChange({ headers: h })} /></div>
          <div className="guide-callout" style={{ marginTop: 10 }}>
            <strong>Request Mapping</strong>
            <div className="workflow-content-segments" role="group" aria-label="Webhook mapping target" style={{ marginTop: 8 }}>
              {(['url', 'body', 'header'] as const).map(target => <button key={target} type="button" aria-pressed={webhookVariableTarget === target} onClick={() => setWebhookVariableTarget(target)}>{target === 'url' ? 'URL' : target === 'body' ? 'Body' : 'Header'}</button>)}
            </div>
            {webhookVariableTarget === 'header' && <div className="form-group" style={{ marginTop: 8 }}>
              <label>Header Name</label>
              <input value={webhookHeaderTarget} onChange={event => setWebhookHeaderTarget(event.target.value)} placeholder="X-Correlation-Id" />
              {/authorization|api[-_]?key|token|secret|credential/i.test(webhookHeaderTarget) && <p className="help-text">Credential headers are managed through outbound integration secrets.</p>}
            </div>}
            <WorkflowVariableBrowser workflowIntegrationId={workflowIntegrationId} sourceType={sourceType}
              workflowSteps={workflowStepsWithResponseMappings} currentStepOrder={currentStepOrder}
              globalVariableNames={globalVariableNames}
              targetLabel={webhookVariableTarget === 'url' ? 'URL' : webhookVariableTarget === 'body' ? 'Body' : 'Header'}
              onSelect={insertWebhookVariable} />
          </div>
          <GlobalVariableMappingsEditor globalVariableNames={globalVariableNames} allowInherited={isSubWorkflow}
            mappings={config.globalVariableMappings} onChange={globalVariableMappings => onChange({ globalVariableMappings })} />
          <div className="help-text" style={{ marginTop: 6 }}>
            Validation constraints: method must be one of GET/POST/PUT/PATCH/DELETE/HEAD/OPTIONS; timeout 1-300s; configured retries 0-10 (runtime bounded to five retries); retry delay 1-300s; header names cannot be blank.
          </div>
          <div className="help-text" style={{ marginTop: 6 }}>
            Webhook/API output variables available to subsequent Condition steps: `step.&lt;order&gt;.responseStatusCode`, `step.&lt;order&gt;.responseBody`, `step.&lt;order&gt;.attempts`, `step.&lt;order&gt;.success`.
          </div>
          <div className="guide-callout" style={{ marginTop: 10 }}>
            <strong>Mode: Inline API/Webhook</strong>
            <div className="help-text" style={{ marginTop: 6 }}>
              This step is currently authored as inline workflow config and can be promoted to reusable outbound integration mode.
            </div>
            <div className="help-text" style={{ marginTop: 6 }}>
              Promotion eligibility:{' '}
              <strong style={{ color: promotionEligibilityColor }}>{promotionEligibilityState}</strong>
              {!canAttemptPromotion && promotionPrecheckMessages.length > 0 && (
                <span>{` (${promotionPrecheckMessages.length} blocker${promotionPrecheckMessages.length === 1 ? '' : 's'})`}</span>
              )}
            </div>
            <div className="help-text" style={{ marginTop: 6 }}>
              Draft state: <strong style={{ color: draftStateColor }}>{draftStateLabel}</strong>
            </div>
          </div>
          <div className="guide-callout" style={{ marginTop: 10 }}>
            <strong>Live Test</strong>
            <div className="help-text" style={{ marginTop: 6 }}>
              Executes this step through relay-backed API/Webhook test flow using current config and optional variable values.
            </div>
            <textarea
              value={webhookTestVariablesInput}
              onChange={e => setWebhookTestVariablesInput(e.target.value)}
              rows={5}
              placeholder={'{\n  "alert.title": "Router Down",\n  "alert.description": "WAN gateway unreachable"\n}'}
              style={{ marginTop: 8, width: '100%' }}
            />
            <div style={{ marginTop: 8 }}>
              <button type="button" className="btn-secondary btn-sm" onClick={runWebhookLiveTest} disabled={!canRunWebhookLiveTest}>
                {webhookTestRunning ? 'Testing...' : 'Run Live Test'}
              </button>
            </div>
            <div className="guide-callout" style={{ marginTop: 8, marginBottom: 0 }}>
              <strong>Live Test Pre-check</strong>
              <div style={{ marginTop: 6 }}>
                URL: <code>{webhookUrl || '(not set)'}</code>
              </div>
              <div style={{ marginTop: 6 }}>
                Variables JSON: <code>{webhookVariablesParseError ? 'invalid' : 'valid'}</code>
              </div>
              {liveTestPrecheckMessages.length > 0 ? (
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: '1.2rem' }}>
                  {liveTestPrecheckMessages.map((message, idx) => <li key={idx}>{message}</li>)}
                </ul>
              ) : (
                <div className="help-text" style={{ marginTop: 8 }}>
                  Live test request is ready.
                </div>
              )}
            </div>
            {webhookTestError && <div className="error-message" style={{ marginTop: 8 }}>{webhookTestError}</div>}
            {webhookTestResult && (
              <div style={{ marginTop: 8 }}>
                <div className="help-text">
                  Status: <strong>{webhookTestResult.statusCode}</strong> | Success: <strong>{String(webhookTestResult.success)}</strong> | Latency: <strong>{webhookTestResult.latencyMs}ms</strong>
                </div>
                {webhookTestResult.errorCode && (
                  <div className="help-text" style={{ marginTop: 4 }}>
                    Error: <code>{webhookTestResult.errorCode}</code>{webhookTestResult.errorMessage ? ` - ${webhookTestResult.errorMessage}` : ''}
                  </div>
                )}
                <pre className="hiw-pre" style={{ marginTop: 8 }}>{webhookTestResult.responseBodyPreview || '(empty response body)'}</pre>
                {Array.isArray(webhookTestResult.mappingCandidates) && webhookTestResult.mappingCandidates.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div className="help-text" style={{ marginBottom: 6 }}>
                      Mapping candidates (JSONPath):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                      {webhookTestResult.mappingCandidates.slice(0, 24).map((candidate: string) => (
                        <button
                          key={candidate}
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => onChange({ bodyTemplate: `${config.bodyTemplate || ''}\n${candidate}`.trim() })}
                          title="Append candidate path to body template"
                        >
                          {candidate}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="guide-callout" style={{ marginTop: 10 }}>
            <strong>Promote to Outbound Integration</strong>
            <div className="help-text" style={{ marginTop: 6 }}>
              Convert this inline Webhook/API step into a reusable outbound integration action and rebind the step.
            </div>
            <div className="form-row" style={{ marginTop: 8 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Integration Name</label>
                <input
                  type="text"
                  value={promotionIntegrationName}
                  onChange={e => setPromotionIntegrationName(e.target.value)}
                  placeholder="PagerDuty Incident API"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Provider</label>
                <input
                  type="text"
                  value={promotionProvider}
                  onChange={e => setPromotionProvider(e.target.value)}
                  placeholder="CustomWebhook"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Action Name (optional)</label>
                <input
                  type="text"
                  value={promotionActionName}
                  onChange={e => setPromotionActionName(e.target.value)}
                  placeholder="Create Ticket"
                />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={promoteWebhookToOutbound}
                disabled={!canAttemptPromotion}
              >
                {promotionRunning ? 'Promoting...' : 'Promote to Outbound'}
              </button>
            </div>

            <div className="guide-callout" style={{ marginTop: 8, marginBottom: 0 }}>
              <strong>Promotion Pre-check</strong>
              <div style={{ marginTop: 6 }}>
                Source URL: <code>{webhookUrl || '(not set)'}</code>
              </div>
              <div style={{ marginTop: 6 }}>
                Target Integration: <code>{integrationName || '(not set)'}</code>
              </div>
              <div style={{ marginTop: 6 }}>
                Provider: <code>{normalizedProvider}</code>
              </div>
              <div style={{ marginTop: 6 }}>
                Action Name: <code>{actionName}</code>
              </div>
              {promotionPrecheckMessages.length > 0 ? (
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: '1.2rem' }}>
                  {promotionPrecheckMessages.map((message, idx) => <li key={idx}>{message}</li>)}
                </ul>
              ) : (
                <div className="help-text" style={{ marginTop: 8 }}>
                  Promotion request is ready.
                </div>
              )}
            </div>

            {promotionError && <div className="error-message" style={{ marginTop: 8 }}>{promotionError}</div>}
            {promotionResult && (
              <div className="guide-callout" style={{ marginTop: 8, marginBottom: 0 }}>
                <strong>Promotion Completed</strong>
                <div className="help-text" style={{ marginTop: 6 }}>
                  Step has been converted to <code>{promotionResult.promotedStepType || 'IntegrationAction'}</code> and rebound to the newly created integration action.
                </div>
                <div style={{ marginTop: 8 }}>
                  Integration: <code>{promotionResult.integrationName}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  Action: <code>{promotionResult.actionName}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  Pinned Version: <code>{promotionResult.integrationVersion || 'v1'}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  Integration ID: <code>{promotionResult.integrationId}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  Action ID: <code>{promotionResult.actionId}</code>
                </div>
                <div className="help-text" style={{ marginTop: 8 }}>
                  Save workflow steps to persist editor state after refresh.
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      if (navigator?.clipboard?.writeText) {
                        navigator.clipboard.writeText(promotionResult.integrationId).catch(() => undefined);
                      }
                    }}
                  >
                    Copy Integration ID
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      if (navigator?.clipboard?.writeText) {
                        navigator.clipboard.writeText(promotionResult.actionId).catch(() => undefined);
                      }
                    }}
                  >
                    Copy Action ID
                  </button>
                </div>
              </div>
            )}
            {(promotionError || promotionResult) && (
              <div style={{ marginTop: 8, display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {promotionError && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={promoteWebhookToOutbound}
                    disabled={!canAttemptPromotion}
                  >
                    Retry Promotion
                  </button>
                )}
                {!!promotionResult && (
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      if (navigator?.clipboard?.writeText) {
                        navigator.clipboard.writeText(promotionSummaryText).catch(() => undefined);
                      }
                    }}
                  >
                    Copy Confirmation
                  </button>
                )}
                <button type="button" className="btn-secondary btn-sm" onClick={clearPromotionState}>
                  Clear Result
                </button>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => {
                    setPromotionProvider('CustomWebhook');
                    setPromotionActionName('');
                    clearPromotionState();
                  }}
                >
                  Reset Promotion Inputs
                </button>
              </div>
            )}
          </div>
        </>
      );
    }
    case 'DispatchOutboundDelivery':
    {
      const deliveryId = `delivery-${currentStepId || currentStepOrder}`;
      const integrations = (Array.isArray(outboundIntegrations) ? outboundIntegrations : []).filter(item => item.isEnabled !== false);
      const actions = (outboundIntegrationActionsByIntegration[config.integrationId || ''] || [])
        .filter(action => action.isEnabled && action.isDeliveryCapable === true && (!notificationSource || isNotificationSafeAction(action)));
      const selected = actions.find(action => action.outboundIntegrationActionId === config.integrationActionId);
      const inputs = selected?.deliveryInputs || [];
      const mappings: Record<string, string> = config.fieldMappings || {};
      const target = inputs.some(field => field.name === integrationMappingTarget) ? integrationMappingTarget : inputs[0]?.name || '';
      return <>
        <div className="form-group">
          <label htmlFor={`${deliveryId}-integration`}>Outbound integration</label>
          <select id={`${deliveryId}-integration`} value={config.integrationId || ''}
            onChange={event => onChange({ integrationId: event.target.value, integrationActionId: '', fieldMappings: {} })}>
            <option value="">Select integration</option>
            {integrations.map(item => <option key={item.outboundIntegrationId} value={item.outboundIntegrationId}>{item.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor={`${deliveryId}-action`}>Delivery-capable action</label>
          <select id={`${deliveryId}-action`} data-delivery-action value={config.integrationActionId || ''} disabled={!config.integrationId}
            onChange={event => onChange({ integrationActionId: event.target.value, fieldMappings: {} })}>
            <option value="">Select delivery action</option>
            {actions.map(action => <option key={action.outboundIntegrationActionId} value={action.outboundIntegrationActionId}>{action.name}</option>)}
          </select>
          {config.integrationId && !actions.length && <div role="status">No enabled delivery-capable actions.</div>}
          {config.integrationActionId && !selected && <div role="alert">The selected action is unavailable or no longer delivery-capable. Choose an eligible action.</div>}
        </div>
        {selected && <fieldset style={{ minWidth: 0 }}>
          <legend>Delivery input mappings</legend>
          {inputs.map((field, index) => {
            const literal = String(mappings[field.name] || '').startsWith('static:');
            const value = mappings[field.name] || '';
            return <div className="form-group" key={field.name}>
            <label htmlFor={`${deliveryId}-input-${index}`}>{field.name} {field.required ? '(required)' : '(optional)'}</label>
            <select aria-label={`${field.name} value type`} value={literal ? 'literal' : 'variable'}
              onChange={event => onChange({ fieldMappings: { ...mappings, [field.name]: event.target.value === 'literal'
                ? `static:${value}` : value.replace(/^static:/, '') } })}>
              <option value="variable">Variable</option><option value="literal">Literal value</option>
            </select>
            <input id={`${deliveryId}-input-${index}`} data-delivery-field={field.name} value={literal ? value.slice(7) : value} required={field.required}
              placeholder={literal ? 'Value' : 'event.title'}
              aria-invalid={field.required && !(literal ? value.slice(7) : value).trim()}
              onFocus={() => setIntegrationMappingTarget(field.name)}
              onChange={event => onChange({ fieldMappings: { ...mappings, [field.name]: `${literal ? 'static:' : ''}${event.target.value}` } })} />
          </div>;
          })}
          <div className="form-group">
            <label htmlFor={`${deliveryId}-mapping-target`}>Mapping target</label>
            <select id={`${deliveryId}-mapping-target`} value={target} onChange={event => setIntegrationMappingTarget(event.target.value)}>
              {inputs.map(field => <option key={field.name} value={field.name}>{field.name}</option>)}
            </select>
          </div>
          <WorkflowVariableBrowser workflowIntegrationId={workflowIntegrationId} sourceType={sourceType}
            workflowSteps={workflowStepsWithResponseMappings} currentStepOrder={currentStepOrder} globalVariableNames={globalVariableNames}
            targetLabel={target || 'Delivery field'} onSelect={path => { if (target) onChange({ fieldMappings: { ...mappings, [target]: path } }); }} />
        </fieldset>}
        <div className="form-group">
          <label htmlFor={`${deliveryId}-timeout`}>Timeout (seconds)</label>
          <input id={`${deliveryId}-timeout`} type="number" min={1} max={300} value={config.timeoutSeconds ?? 60}
            onChange={event => onChange({ timeoutSeconds: Number(event.target.value) })} />
        </div>
        <GlobalVariableMappingsEditor globalVariableNames={globalVariableNames} allowInherited={isSubWorkflow}
          mappings={config.globalVariableMappings} onChange={globalVariableMappings => onChange({ globalVariableMappings })} />
      </>;
    }
    case 'IntegrationAction':
    {
      const ticketingIntegrations = (Array.isArray(outboundIntegrations) ? outboundIntegrations : []).filter(i => i.isEnabled !== false);
      const selectedIntegrationId = config.integrationId || '';
      const selectedActions = selectedIntegrationId ? (outboundIntegrationActionsByIntegration[selectedIntegrationId] || []) : [];
      const selectedAction = selectedActions.find(a => a.outboundIntegrationActionId === (config.integrationActionId || ''));
      const selectedIntegration = ticketingIntegrations.find(i => i.outboundIntegrationId === selectedIntegrationId);
      const fieldMappings = config.fieldMappings || {};
      const expectedFieldKeys = selectedAction
        ? extractIntegrationFieldTokens(
            selectedAction.pathTemplate,
            selectedAction.bodyTemplate,
            ...Object.values(selectedAction.headers || {}),
          )
        : [];
        const expectedBodyPaths = getOutboundActionSchemaPaths(selectedAction);
        const mappingTargets = Array.from(new Set([...expectedFieldKeys, ...expectedBodyPaths, ...Object.keys(config.fieldMappings || {})]))
        .map(key => key.trim()).filter(Boolean);
      const mappingTarget = mappingTargets.includes(integrationMappingTarget)
        ? integrationMappingTarget : mappingTargets[0] || '';
      const mappedFieldKeys = Object.keys(fieldMappings)
        .map((k) => k.trim())
        .filter((k) => k.length > 0);
      const missingFieldKeys = expectedFieldKeys.filter((key) => !mappedFieldKeys.includes(key));
      const extraMappedFieldKeys = mappedFieldKeys.filter((key) => expectedFieldKeys.length > 0 &&
        !expectedFieldKeys.includes(key) && !expectedBodyPaths.includes(key));
      const invalidFieldMappings = Object.entries(fieldMappings).filter(([key, value]) => !key.trim() || !String(value || '').trim());
      const hasMappingParityIssues = expectedFieldKeys.length > 0 && (missingFieldKeys.length > 0 || invalidFieldMappings.length > 0);

      const upsertFieldMapping = (fieldKey: string) => {
        const existingValue = String(fieldMappings[fieldKey] || '').trim();
        onChange({
          fieldMappings: {
            ...fieldMappings,
            [fieldKey]: existingValue || inferWorkflowVariableForFieldKey(fieldKey),
          },
        });
      };

      const applySuggestedFieldMappings = () => {
        const nextMappings: Record<string, string> = { ...fieldMappings };
        missingFieldKeys.forEach((fieldKey) => {
          const existingValue = String(nextMappings[fieldKey] || '').trim();
          if (!existingValue) {
            nextMappings[fieldKey] = inferWorkflowVariableForFieldKey(fieldKey);
          }
        });
        onChange({ fieldMappings: nextMappings });
      };

      const currentPinnedVersion = (config.integrationVersion || 'v1').trim();
      const effectivePinnedVersion = hasExplicitRebindVersion ? trimmedRebindVersion : undefined;
      const selectedActionId = (config.integrationActionId || '').trim();
      const canAttemptRebind =
        !rebindRunning &&
        !isSubWorkflow &&
        (!notificationSource || (!!selectedAction && isNotificationSafeAction(selectedAction))) &&
        !!currentStepId &&
        !!selectedIntegrationId &&
        !!selectedActionId &&
        rebindVersionIsValid &&
        !hasMappingParityIssues;
      const targetChanged = selectedIntegrationId !== '' && selectedIntegrationId !== (config.integrationId || '');
      const actionChanged = selectedActionId !== '' && selectedActionId !== (config.integrationActionId || '');
      const versionChanged = !!effectivePinnedVersion && effectivePinnedVersion !== currentPinnedVersion;
      const hasPlannedChange = targetChanged || actionChanged || versionChanged;
      const rebindSummaryText = rebindResult
        ? `Rebound to ${rebindResult.integrationName}${rebindResult.integrationVersion ? ` @ ${rebindResult.integrationVersion}` : ''} (${rebindResult.actionName}).`
        : '';

      const rebindPrecheckMessages: string[] = [];
      if (isSubWorkflow) {
        rebindPrecheckMessages.push('Rebind is disabled for sub-workflows until the API supports revision-guarded rebind.');
      }
      if (!selectedIntegrationId) {
        rebindPrecheckMessages.push('Select an enabled integration.');
      }
      if (selectedIntegrationId && !selectedActionId) {
        rebindPrecheckMessages.push('Select an action for the integration.');
      }
      if (selectedIntegrationId && selectedActionId && !selectedAction) {
        rebindPrecheckMessages.push('Selected action is unavailable. Refresh actions or choose another action.');
      }
      if (hasExplicitRebindVersion && !rebindVersionIsValid) {
        rebindPrecheckMessages.push('Pinned version format is invalid.');
      }
      if (hasMappingParityIssues) {
        rebindPrecheckMessages.push('Resolve field mapping parity issues before rebinding.');
      }
      if (state.isDirty) {
        rebindPrecheckMessages.push('Save workflow step changes before rebinding.');
      }
      if (canAttemptRebind && !hasPlannedChange) {
        rebindPrecheckMessages.push('No explicit change detected. Rebind will re-confirm current selection/version.');
      }

      return (
        <>
          <div className="form-group">
            <label>Integration</label>
            <select value={selectedIntegrationId} onChange={e => onChange({ integrationId: e.target.value, integrationActionId: '' })}>
              <option value="">-- Select Integration --</option>
              {ticketingIntegrations.map(i => <option key={i.outboundIntegrationId} value={i.outboundIntegrationId}>{i.name} ({i.provider})</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Integration Action</label>
            <select
              value={config.integrationActionId || ''}
              onChange={e => onChange({ integrationActionId: e.target.value })}
              disabled={!selectedIntegrationId}
            >
              <option value="">-- Select Action --</option>
              {selectedActions.map(a => (
                <option key={a.outboundIntegrationActionId} value={a.outboundIntegrationActionId}
                  disabled={notificationSource && !isNotificationSafeAction(a)}>
                  {a.name} ({a.method} {a.pathTemplate || '/'}){notificationSource && !isNotificationSafeAction(a) ? ' - ticket action unavailable' : ''}
                </option>
              ))}
            </select>
            <div className="help-text" style={{ marginTop: 6 }}>
              Actions are managed from Integrations {'->'} Outbound {'->'} Actions.
            </div>
            {selectedAction?.deliveryProfile && (
              <div className="guide-callout" role="status" aria-label="Outbound delivery profile requirements" style={{ marginTop: 8 }}>
                <strong>{selectedAction.deliveryProfile.profile}</strong>
                <div style={{ marginTop: 6 }}>{selectedAction.deliveryProfile.description}</div>
                <div style={{ marginTop: 6 }}>
                  Required fields: <code>{selectedAction.deliveryProfile.requiredFields.join(', ')}</code>
                </div>
                {selectedAction.deliveryProfile.supportsAttachments && (
                  <div style={{ marginTop: 6 }}>
                    Attachments: up to {selectedAction.deliveryProfile.maxAttachmentCount ?? 'configured limit'};
                    {' '}{selectedAction.deliveryProfile.maxBytesPerAttachment
                      ? `${Math.floor(selectedAction.deliveryProfile.maxBytesPerAttachment / (1024 * 1024))} MiB each`
                      : 'size limit enforced by the server'}.
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="btn-secondary btn-sm"
              aria-label="Toggle integration binding actions"
              onClick={() => setShowBindingActions(value => !value)}
            >
              {showBindingActions ? 'Hide' : 'Show'}
            </button>
          </div>
          {showBindingActions && (
            <>
              <div className="form-row">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Pinned Version</label>
                  <input
                    type="text"
                    value={rebindVersion}
                    onChange={e => setRebindVersion(e.target.value)}
                    placeholder="v1"
                  />
                  {!rebindVersionIsValid && (
                    <div className="error-message" style={{ marginTop: 6 }}>
                      Version must match <code>[A-Za-z0-9][A-Za-z0-9._-]{'{0,63}'}</code>
                    </div>
                  )}
                  <div className="help-text" style={{ marginTop: 6 }}>
                    Use semantic tags like <code>v1</code>, <code>v1.1</code>, or <code>2026-06</code> for explicit workflow pinning.
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'end' }}>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={rebindIntegrationActionStep}
                    disabled={!canAttemptRebind}
                  >
                    {rebindRunning ? 'Rebinding...' : 'Rebind Step to Selection'}
                  </button>
                </div>
              </div>

              <div className="guide-callout" style={{ marginTop: 8, marginBottom: 10 }}>
                <strong>Rebind Pre-check</strong>
                <div style={{ marginTop: 6 }}>
                  Target Integration: <code>{selectedIntegration ? `${selectedIntegration.name} (${selectedIntegration.provider})` : '(not selected)'}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  Target Action: <code>{selectedAction ? selectedAction.name : '(not selected)'}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  Current Pinned Version: <code>{currentPinnedVersion || 'v1'}</code>
                </div>
                <div style={{ marginTop: 6 }}>
                  Requested Pinned Version: <code>{effectivePinnedVersion || '(keep existing)'}</code>
                </div>
                {expectedFieldKeys.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    Mapped Fields: <code>{`${expectedFieldKeys.length - missingFieldKeys.length}/${expectedFieldKeys.length}`}</code>
                  </div>
                )}
                {rebindPrecheckMessages.length > 0 ? (
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: '1.2rem' }}>
                    {rebindPrecheckMessages.map((message, idx) => <li key={idx}>{message}</li>)}
                  </ul>
                ) : (
                  <div className="help-text" style={{ marginTop: 8 }}>
                    Rebind request is ready.
                  </div>
                )}
              </div>

              {rebindError && <div className="error-message" style={{ marginTop: 8 }}>{rebindError}</div>}
              {rebindResult && (
                <div className="guide-callout" style={{ marginTop: 8, marginBottom: 0 }}>
                  <strong>Rebind Completed</strong>
                  <div className="help-text" style={{ marginTop: 6 }}>
                    Step is now bound to <code>{rebindResult.reboundStepType || 'IntegrationAction'}</code> target selection.
                  </div>
                  <div style={{ marginTop: 8 }}>
                    Integration: <code>{rebindResult.integrationName}</code>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    Action: <code>{rebindResult.actionName}</code>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    Pinned Version: <code>{rebindResult.integrationVersion || 'v1'}</code>
                  </div>
                  <div className="help-text" style={{ marginTop: 8 }}>
                    Save workflow steps to persist editor state after refresh.
                  </div>
                </div>
              )}
              {(rebindError || rebindResult) && (
                <div style={{ marginTop: 8, display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {rebindError && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={rebindIntegrationActionStep}
                      disabled={!canAttemptRebind}
                    >
                      Retry Rebind
                    </button>
                  )}
                  {!!rebindResult && (
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        if (navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(rebindSummaryText).catch(() => undefined);
                        }
                      }}
                    >
                      Copy Rebind Confirmation
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      setRebindError(null);
                      setRebindResult(null);
                    }}
                  >
                    Clear Rebind Result
                  </button>
                </div>
              )}
              {selectedAction ? (
                <div className="guide-callout" style={{ marginBottom: 10 }}>
                  <strong>Action Definition</strong>
                  <div style={{ marginTop: 6 }}>Using action preset: <strong>{selectedAction.name}</strong></div>
                  <div style={{ marginTop: 6 }}>Method: <code>{selectedAction.method || '-'}</code></div>
                  <div style={{ marginTop: 6 }}>Path: <code>{selectedAction.pathTemplate || '/'}</code></div>
                  <div style={{ marginTop: 6 }}>Action Key: <code>{selectedAction.actionKey || '-'}</code></div>
                  <div style={{ marginTop: 6 }}>Ticket ID Variable: <code>{selectedAction.ticketIdVariable || '(auto-parse common fields)'}</code></div>
                  <div style={{ marginTop: 6 }}>{config.definitionSnapshot ? 'Pinned Version' : 'Definition'}: <code>{config.definitionSnapshot ? config.integrationVersion : 'Live'}</code></div>
                  <div className="help-text" style={{ marginTop: 8 }}>
                    Edit method/path/body/headers/ticket parsing in Integrations {'->'} Outbound {'->'} Actions.
                  </div>
                </div>
              ) : (
                <div className="guide-callout" style={{ marginBottom: 10 }}>
                  <strong>Select an Integration Action</strong>
                  <div className="help-text" style={{ marginTop: 8 }}>
                    Workflow-level inline action editing has been moved to Integration Actions. Choose an action above, or create one in Integrations {'->'} Outbound {'->'} Actions.
                  </div>
                  {(config.pathTemplate || config.bodyTemplate || config.method || config.actionKey || config.ticketIdVariable) && (
                    <div className="help-text" style={{ marginTop: 8 }}>
                      Legacy inline action config is present in this step and will still execute, but new edits should be done in Integration Actions.
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <div className="form-group">
            <label>Outbound Request Field Mappings (optional)</label>
            {expectedBodyPaths.length > 0 && <p className="help-text">Selectable JSON Pointer targets are inferred from the selected action body template. A mapping overrides that request field with the chosen alert or notification value.</p>}
            <KeyValueEditor value={fieldMappings} onChange={h => onChange({ fieldMappings: h })} addLabel="+ Add Mapping" />
            {mappingTargets.length > 0 && <>
              <div className="form-group" style={{ marginTop: 8 }}>
                <label>Outbound field to map</label>
                <select value={mappingTarget} onChange={event => setIntegrationMappingTarget(event.target.value)}>
                  {mappingTargets.map(field => <option key={field} value={field}>{field}</option>)}
                </select>
              </div>
              <WorkflowVariableBrowser workflowIntegrationId={workflowIntegrationId} sourceType={sourceType}
                workflowSteps={workflowStepsWithResponseMappings} currentStepOrder={currentStepOrder} targetLabel={mappingTarget || 'Outbound field'}
                globalVariableNames={globalVariableNames}
                onSelect={path => mappingTarget && onChange({ fieldMappings: { ...fieldMappings, [mappingTarget]: path } })} />
            </>}
            {selectedAction && expectedFieldKeys.length > 0 && (
              <div className="guide-callout" style={{ marginTop: 8, marginBottom: 0 }}>
                <strong>Provider Mapping Parity</strong>
                <div className="help-text" style={{ marginTop: 6 }}>
                  This action template references <strong>{expectedFieldKeys.length}</strong> integration field token(s). Map each expected field for predictable provider payloads.
                </div>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: 8 }}>
                  {expectedFieldKeys.map((fieldKey) => {
                    const isMapped = String(fieldMappings[fieldKey] || '').trim().length > 0;
                    return (
                      <button
                        key={fieldKey}
                        type="button"
                        className="btn-secondary btn-sm"
                        style={{ opacity: isMapped ? 1 : 0.85 }}
                        onClick={() => upsertFieldMapping(fieldKey)}
                        title={isMapped ? 'Mapped field' : 'Add suggested mapping'}
                      >
                        {isMapped ? '\u2713 ' : '+ '}
                        {fieldKey}
                      </button>
                    );
                  })}
                </div>
                {missingFieldKeys.length > 0 && (
                  <div className="error-message" style={{ marginTop: 8 }}>
                    Missing mappings: {missingFieldKeys.join(', ')}
                  </div>
                )}
                {extraMappedFieldKeys.length > 0 && (
                  <div className="help-text" style={{ marginTop: 8 }}>
                    Unused mapping keys (not referenced by current templates): {extraMappedFieldKeys.join(', ')}
                  </div>
                )}
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={applySuggestedFieldMappings}
                    disabled={missingFieldKeys.length === 0}
                  >
                    Apply Suggested Mappings
                  </button>
                </div>
              </div>
            )}
            {invalidFieldMappings.length > 0 && (
              <div className="error-message" style={{ marginTop: 8 }}>
                Field mappings cannot have blank keys or values.
              </div>
            )}
            <div className="help-text" style={{ marginTop: 6 }}>
              Map workflow variables into action templates. Example mapping: <code>details</code> {'->'} <code>alert.description</code> enables <code>{'{{integration.fields.details}}'}</code> in action body templates.
            </div>
          </div>
          <GlobalVariableMappingsEditor globalVariableNames={globalVariableNames} allowInherited={isSubWorkflow}
            mappings={config.globalVariableMappings} onChange={globalVariableMappings => onChange({ globalVariableMappings })} />
          <div className="form-row" style={{ marginTop: 8 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Retry Count</label>
              <input aria-label="Integration Action retry count" type="number" min={0} max={5} value={config.retryCount ?? 0}
                onChange={event => onChange({ retryCount: Math.max(0, Math.min(5, parseInt(event.target.value) || 0)) })} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Retry Delay (seconds)</label>
              <input aria-label="Integration Action retry delay" type="number" min={1} max={300} value={config.retryDelaySeconds ?? 5}
                onChange={event => onChange({ retryDelaySeconds: Math.max(1, Math.min(300, parseInt(event.target.value) || 5)) })} />
            </div>
          </div>
          <div className="help-text" style={{ marginTop: 6 }}>
            Integration Action uses outbound integration auth secrets automatically. For updateTicket and closeTicket, {'{{integration.ticketId}}'} resolves from the latest ticket association for this alert. Alert tokens such as {'{{alert.description}}'} and {'{{alert.data.details}}'} are resolved in action templates.
          </div>
        </>
      );
    }
    case 'UpdateAlert':
      return (
        <>
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select value={config.status || ''} onChange={e => onChange({ status: e.target.value })}>
                <option value="">(No change)</option><option value="Acknowledged">Acknowledge</option><option value="Closed">Close</option>
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select value={config.priority || ''} onChange={e => onChange({ priority: e.target.value })}>
                <option value="">(No change)</option>
                {SEVERITY_OPTIONS.map(sev => <option key={sev} value={sev}>{sev}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Assign To (User GUID compatibility)</label>
              <select value={config.assignTo || ''} onChange={e => onChange({ assignTo: e.target.value })}>
                <option value="">(No change)</option>
                {users.map(u => <option key={u.userId} value={u.userId}>{u.displayName}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Assign Target Type</label>
              <select
                value={config.assignTargetType || ''}
                onChange={e => onChange({ assignTargetType: e.target.value, assignTargetId: '' })}
              >
                <option value="">(No change)</option>
                <option value="User">User</option>
                <option value="Group">Group</option>
                <option value="OnCall">OnCall</option>
              </select>
            </div>
            <div className="form-group">
              <label>Assign Target</label>
              {config.assignTargetType === 'Group' ? (
                <select value={config.assignTargetId || ''} onChange={e => onChange({ assignTargetId: e.target.value })}>
                  <option value="">(Select group)</option>
                  {groups.map(g => <option key={g.groupId} value={g.groupId}>{g.name}</option>)}
                </select>
              ) : config.assignTargetType === 'OnCall' ? (
                <select value={config.assignTargetId || ''} onChange={e => onChange({ assignTargetId: e.target.value })}>
                  <option value="">(Select schedule)</option>
                  {schedules.map(s => <option key={s.scheduleId} value={s.scheduleId}>{s.name}</option>)}
                </select>
              ) : config.assignTargetType === 'User' ? (
                <select value={config.assignTargetId || ''} onChange={e => onChange({ assignTargetId: e.target.value })}>
                  <option value="">(Select user)</option>
                  {users.map(u => <option key={u.userId} value={u.userId}>{u.displayName || u.email || u.userId}</option>)}
                </select>
              ) : (
                <input disabled value="Choose target type first" />
              )}
            </div>
          </div>
        </>
      );
    default: return <div className="help-text">No configuration needed.</div>;
  }
};


// ─── Instance History Modal ─────────────────────────────────────────────────

const InstanceHistoryModal: React.FC<{ workflow: WorkflowDto; onClose: () => void; }> = ({ workflow, onClose }) => {
  const { ask, dialog } = useWorkflowDialog();
  useDateTimePreferences();
  const [instances, setInstances] = useState<WorkflowInstanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const [cancelFeedback, setCancelFeedback] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const loadInstances = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tenantApi.getWorkflowInstances(workflow.workflowId);
      setInstances(data.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [workflow.workflowId]);

  useEffect(() => { loadInstances(); }, [loadInstances]);

  const handleCancel = async (instanceId: string) => {
    if (await ask({ title: 'Cancel workflow instance?', message: 'Cancel this running workflow instance?', action: 'Cancel instance', destructive: true }) === null) return;
    try {
      setCancelFeedback(null);
      setCancelError(null);
      const result = await tenantApi.cancelWorkflowInstance(instanceId);
      setCancelFeedback(result.status === 'CancellationPending'
        ? 'Cancellation was requested, but Durable has not confirmed termination. The instance remains active until confirmation.'
        : 'Durable confirmed termination; the workflow is cancelled.');
      await loadInstances();
    } catch (err) { setCancelError(err instanceof Error ? err.message : 'Workflow cancellation failed.'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      {dialog}
      <div className="modal" style={{ maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
        <h3>Execution History: {workflow.name}</h3>
        {cancelFeedback && <div role="status" className="help-text">{cancelFeedback}</div>}
        {cancelError && <div role="alert" className="error-message">{cancelError}</div>}
        <div className="form-actions">
          <button type="button" className="btn-secondary btn-sm" onClick={loadInstances} disabled={loading}>Refresh</button>
        </div>
        {loading ? ( <div className="loading">Loading...</div> ) : (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <table className="data-table">
              <thead><tr><th>Started</th><th>Status</th><th>Duration</th><th>Trigger</th><th>Error</th><th></th></tr></thead>
              <tbody>
                {instances.map(inst => (
                  <React.Fragment key={inst.instanceId}>
                    <tr onClick={() => setSelectedInstanceId(p => p === inst.instanceId ? null : inst.instanceId)} style={{ cursor: 'pointer' }}>
                      <td>{formatDateTime(inst.startedAt)}</td>
                      <td><StatusBadge status={inst.status} /></td>
                      <td>{inst.completedAt ? `${Math.round((new Date(inst.completedAt).getTime() - new Date(inst.startedAt).getTime()) / 1000)}s` : '-'}</td>
                      <td>{inst.triggerType}</td>
                      <td style={{ color: 'var(--color-severity-critical)', fontSize: '0.8rem' }}>{inst.errorMessage}</td>
                      <td>
                        {inst.status === 'Running' && <button className="btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); handleCancel(inst.instanceId); }}>Cancel</button>}
                        {selectedInstanceId === inst.instanceId ? '▲' : '▼'}
                      </td>
                    </tr>
                    {selectedInstanceId === inst.instanceId && (
                      <tr><td colSpan={6}><InstanceDetail instanceId={inst.instanceId} /></td></tr>
                    )}
                  </React.Fragment>
                ))}
                {instances.length === 0 && <tr><td colSpan={6} className="empty-row">No executions recorded.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <div className="form-actions"><button type="button" className="btn-secondary" onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
};

const InstanceDetail: React.FC<{ instanceId: string }> = ({ instanceId }) => {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof tenantApi.getWorkflowInstance>> | null>(null);
  useEffect(() => { tenantApi.getWorkflowInstance(instanceId).then(setDetail); }, [instanceId]);

  const getStepOutputSummary = (step: WorkflowStateEntryDto) => {
    if (!step.outputJson) return null;

    try {
      const parsed = JSON.parse(step.outputJson);
      const outputVariables = parsed?.outputVariables || {};
      const stepKeyPrefix = `step.${step.stepOrder}.`;
      const ticketId = String(outputVariables[`${stepKeyPrefix}ticketId`] || '').trim();
      const responseStatusCode = String(outputVariables[`${stepKeyPrefix}responseStatusCode`] || '').trim();
      const responseBody = String(outputVariables[`${stepKeyPrefix}responseBody`] || '');

      let ticketNumber = '';
      if (responseBody) {
        try {
          const responseJson = JSON.parse(responseBody);
          if (responseJson && typeof responseJson === 'object') {
            const rawTicketNumber = responseJson.ticket_number ?? responseJson.ticketNumber;
            if (rawTicketNumber !== undefined && rawTicketNumber !== null) {
              ticketNumber = String(rawTicketNumber);
            }
          }
        } catch {
        }
      }

      if (!ticketId && !ticketNumber && !responseStatusCode) return null;

      return {
        ticketId,
        ticketNumber,
        responseStatusCode,
      };
    } catch {
      return null;
    }
  };

  if (!detail) return <div className="loading">Loading detail...</div>;
  return (
    <div style={{ padding: '0.5rem 1rem', background: 'var(--color-background)' }}>
      {detail.instance.resultCode && <p><strong>Result:</strong> {detail.instance.resultCode}</p>}
      {detail.instance.resultOutputsJson && <div><strong>Returned outputs</strong><pre>{detail.instance.resultOutputsJson}</pre></div>}
      <table className="data-table">
        <thead><tr><th>#</th><th>Step ID</th><th>Status</th><th>Duration</th><th>Output</th><th>Error</th></tr></thead>
        <tbody>
          {detail.steps.map(s => (
            <tr key={s.stateId}>
              <td>{s.stepOrder + 1}</td>
              <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{s.stepId.substring(0, 8)}...</td>
              <td><StatusBadge status={s.status} /></td>
              <td>{s.startedAt && s.completedAt ? `${Math.round((new Date(s.completedAt).getTime() - new Date(s.startedAt).getTime()))}ms` : '-'}</td>
              <td style={{ fontSize: '0.8rem' }}>
                {(() => {
                  const summary = getStepOutputSummary(s);
                  if (!summary) return '-';
                  return (
                    <div>
                      {summary.ticketNumber && <div><strong>Ticket #:</strong> {summary.ticketNumber}</div>}
                      {summary.ticketId && <div><strong>Ticket ID:</strong> {summary.ticketId}</div>}
                      {summary.responseStatusCode && <div><strong>HTTP:</strong> {summary.responseStatusCode}</div>}
                    </div>
                  );
                })()}
              </td>
              <td style={{ color: 'var(--color-severity-critical)', fontSize: '0.8rem' }}>{s.errorMessage}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!!detail.subWorkflowInstances?.length && <section aria-label="Linked sub-workflow executions">
        <h4>Linked sub-workflow executions</h4>
        <table className="data-table">
          <thead><tr><th>Workflow</th><th>Status</th><th>Depth</th><th>Result</th><th>Snapshot</th><th>Returned outputs</th></tr></thead>
          <tbody>{detail.subWorkflowInstances.map(subInstance => (
            <tr key={subInstance.instanceId}>
              <td>{subInstance.workflowId.slice(0, 8)}...<small> {subInstance.instanceId.slice(0, 8)}...</small></td>
              <td><StatusBadge status={subInstance.status} /></td>
              <td>{subInstance.callDepth ?? '-'}</td>
              <td>{subInstance.resultCode || '-'}</td>
              <td>{subInstance.subWorkflowSnapshotId ? `${subInstance.subWorkflowSnapshotId.slice(0, 8)}...` : '-'}</td>
              <td><pre>{subInstance.resultOutputsJson || '-'}</pre></td>
            </tr>
          ))}</tbody>
        </table>
      </section>}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colors: Record<string, { background: string; color: string }> = {
    Completed: {
      background: 'rgba(34, 197, 94, 0.2)',
      color: 'var(--color-severity-low)',
    },
    Running: {
      background: 'var(--color-primary-light)',
      color: 'var(--color-primary)',
    },
    Failed: {
      background: 'rgba(239, 68, 68, 0.2)',
      color: 'var(--color-severity-critical)',
    },
    Cancelled: {
      background: 'rgba(251, 191, 36, 0.2)',
      color: 'var(--color-severity-medium)',
    },
  };

  const palette = colors[status] || {
    background: 'var(--color-border-light)',
    color: 'var(--color-text-secondary)',
  };

  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: '0.8rem',
        color: palette.color,
        background: palette.background,
      }}
    >
      {status}
    </span>
  );
};

// ─── Key-Value Editor ─────────────────────────────────────────────────────

const KeyValueEditor: React.FC<{
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  disabled?: boolean;
  addLabel?: string;
}> = ({ value, onChange, disabled = false, addLabel = '+ Add Header' }) => {
  const entries = Object.entries(value);

  const handleKeyChange = (index: number, newKey: string) => {
    const newEntries = [...entries];
    const oldKey = newEntries[index][0];
    const val = newEntries[index][1];
    const updated = { ...value };
    delete updated[oldKey];
    updated[newKey] = val;
    onChange(updated);
  };

  const handleValueChange = (key: string, newValue: string) => {
    onChange({ ...value, [key]: newValue });
  };

  const handleRemove = (key: string) => {
    const { [key]: _, ...rest } = value;
    onChange(rest);
  };

  const handleAdd = () => {
    let newKey = 'new-header';
    let count = 1;
    while (newKey in value) {
      newKey = `new-header-${count++}`;
    }
    onChange({ ...value, [newKey]: '' });
  };

  return (
    <div>
      {entries.map(([key, val], index) => (
        <div key={key} className="form-row" style={{ alignItems: 'center' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <input type="text" value={key} onChange={e => handleKeyChange(index, e.target.value)} disabled={disabled} />
          </div>
          <div className="form-group" style={{ flex: 2 }}>
            <input type="text" value={val} onChange={e => handleValueChange(key, e.target.value)} disabled={disabled} />
          </div>
          <button type="button" className="btn-icon" onClick={() => handleRemove(key)} disabled={disabled}>&times;</button>
        </div>
      ))}
      <button type="button" className="btn-secondary btn-sm" onClick={handleAdd} disabled={disabled}>{addLabel}</button>
    </div>
  );
};

// ─── Workflow Guide ("How It Works") ────────────────────────────────────────

const SubWorkflowGuide: React.FC = () => (
  <div className="escalation-guide">
    <div className="guide-section">
      <h3>How Sub-workflows Work</h3>
      <p>
        Sub-workflows are shared, reusable step bundles that can be called from a workflow using
        the <strong>Run Sub-Workflow</strong> step. They help you centralize common tasks like ticket
        creation, enrichment, and response templating.
      </p>
      <div className="guide-callout">
        <strong>Shared by design:</strong> Updating a sub-workflow definition affects future calls
        from any workflow that references it.
      </div>
    </div>

    <div className="guide-section">
      <h3>Lifecycle and Safety</h3>
      <table className="guide-table">
        <thead>
          <tr>
            <th>Area</th>
            <th>Behavior</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Source Compatibility</strong></td>
            <td>Sub-workflows can be scoped to Alert, Notification, or Both. Only compatible options are selectable in callers.</td>
          </tr>
          <tr>
            <td><strong>Result Codes</strong></td>
            <td>Declared result codes are returned to the caller and can be used for branching or downstream handling.</td>
          </tr>
          <tr>
            <td><strong>In-use Protection</strong></td>
            <td>Disable and delete operations are blocked while a sub-workflow is referenced or actively running.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="guide-section">
      <h3>Recommended Flow</h3>
      <div className="guide-steps">
        <div className="guide-step">
          <div className="guide-step-number">1</div>
          <div className="guide-step-content">
            <h4>Create Shared Definition</h4>
            <p>Use <strong>+ New Sub-Workflow</strong> to add a reusable definition and set compatibility/result contract.</p>
          </div>
        </div>
        <div className="guide-step">
          <div className="guide-step-number">2</div>
          <div className="guide-step-content">
            <h4>Build and Save Steps</h4>
            <p>Open <strong>Edit steps</strong>, build the reusable logic, and save. Keep names and result codes stable for callers.</p>
          </div>
        </div>
        <div className="guide-step">
          <div className="guide-step-number">3</div>
          <div className="guide-step-content">
            <h4>Reference from Workflows</h4>
            <p>Select the sub-workflow in <strong>Run Sub-Workflow</strong> steps and branch on returned status/result values.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const WorkflowGuide: React.FC = () => (
  <div className="escalation-guide">

    <div className="guide-section">
      <h3>How Workflows Work</h3>
      <p>
        Workflows let you automate responses to alerts. When an alert event occurs
        (for example <strong>Alert Created</strong>, <strong>Alert Updated</strong>, <strong>Alert Closed</strong>,
        <strong>Alert Acknowledged</strong>, <strong>Priority Changed</strong>, or <strong>Alert Escalated</strong>),
        Zurvit evaluates all enabled workflows. If the alert matches a workflow's trigger
        conditions, the workflow's steps execute in order -- automatically.
      </p>
      <div className="guide-callout">
        <strong>Set it and forget it:</strong> Once configured, workflows run 24/7
        without human intervention. You can monitor every execution, step outcome,
        and output in the <strong>Execution History</strong> panel.
      </div>
    </div>

    <div className="guide-section">
      <h3>Anatomy of a Workflow</h3>
      <p>
        Every workflow has three parts:
      </p>
      <table className="guide-table">
        <thead>
          <tr>
            <th>Part</th>
            <th>Purpose</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Trigger</strong></td>
            <td>The event that starts the workflow (Alert Created, Alert Updated, or Alert Closed).</td>
          </tr>
          <tr>
            <td><strong>Conditions</strong></td>
            <td>Optional filters (severity, service, source) that narrow which alerts activate the workflow. Leave empty to match all alerts.</td>
          </tr>
          <tr>
            <td><strong>Steps</strong></td>
            <td>The ordered sequence of actions the workflow performs. Steps execute top-to-bottom, with optional branching via Condition steps.</td>
          </tr>
          <tr>
            <td><strong>Secrets (Optional)</strong></td>
            <td>Tenant workflow secrets can be stored in the step builder and referenced as <code>{'{{secret.name}}'}</code> in Webhook/API URLs, headers, or body templates.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="guide-section">
      <h3>Available Step Types</h3>
      <table className="guide-table">
        <thead>
          <tr>
            <th>Step Type</th>
            <th>What It Does</th>
            <th>Key Config</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Send Notification</strong></td>
            <td>Sends an alert notification through a configured channel (Email, SMS, Slack, etc.).</td>
            <td>Select a notification channel.</td>
          </tr>
          <tr>
            <td><strong>Wait / Delay</strong></td>
            <td>Pauses the workflow for a specified number of minutes before continuing.</td>
            <td>Delay in minutes (1+).</td>
          </tr>
          <tr>
            <td><strong>Ping</strong></td>
            <td>Sends a network ping and exposes success/failure and packet results for later branching.</td>
            <td>Target host, optional secret reference, packet count, timeout, success/failure next step.</td>
          </tr>
          <tr>
            <td><strong>Webhook/API</strong></td>
            <td>Sends an HTTP request to an external URL. Useful for integrating with third-party tools.</td>
            <td>URL, HTTP method, timeout, retry count/delay, body template, optional headers. Exposes response status/body/attempt variables for later conditions.</td>
          </tr>
          <tr>
            <td><strong>Integration Action</strong></td>
            <td>Runs a named outbound integration action (create/update/close ticket or custom provider action).</td>
            <td>Select integration + action preset. Request templates and ticket ID extraction are configured on the Integration Action definition.</td>
          </tr>
          <tr>
            <td><strong>Update Alert</strong></td>
            <td>Modifies the alert itself -- change status, priority, or assign to a user.</td>
            <td>Status, priority, and/or assignee.</td>
          </tr>
          <tr>
            <td><strong>Escalate</strong></td>
            <td>Starts or stops the escalation policy for the alert.</td>
            <td>Action: Start/Stop, optional policy selection.</td>
          </tr>
          <tr>
            <td><strong>Condition</strong></td>
            <td>Branches the workflow based on an alert field (e.g. severity equals Critical). Can jump to different steps for true/false.</td>
            <td>Field, operator, value, and target steps for each branch.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div className="guide-section">
      <h3>Creating a Workflow</h3>
      <div className="guide-steps">
        <div className="guide-step">
          <div className="guide-step-number">1</div>
          <div className="guide-step-content">
            <h4>Name and Trigger</h4>
            <p>
              Give the workflow a descriptive name and choose a trigger event
              (<strong>Alert Created</strong>, <strong>Alert Updated</strong>, or <strong>Alert Closed</strong>).
              Optionally narrow the trigger with conditions -- select severities,
              or enter comma-separated service/source filters.
            </p>
          </div>
        </div>
        <div className="guide-step">
          <div className="guide-step-number">2</div>
          <div className="guide-step-content">
            <h4>Build Steps</h4>
            <p>
              After saving the workflow, the step builder opens automatically.
              Click <strong>"+ Add Step"</strong> to insert steps at any position.
              Drag steps to reorder them. Each step has a name, type-specific
              configuration, and an optional <strong>"Continue on error"</strong> flag.
              Workflow Secrets are available as an optional panel and are not a workflow step.
            </p>
          </div>
        </div>
        <div className="guide-step">
          <div className="guide-step-number">3</div>
          <div className="guide-step-content">
            <h4>Save and Enable</h4>
            <p>
              Click <strong>Save Steps</strong> when you're done. The workflow starts
              matching new alerts immediately if enabled. You can disable a workflow
              at any time using the pause button without deleting it.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div className="guide-section">
      <h3>Reusable Sub-Workflows</h3>
      <p>
        Add <strong>Run Sub-Workflow</strong> to await a reusable child workflow. Shared definitions are available across the tenant
        and each run uses an immutable definition snapshot captured when the child is scheduled.
      </p>
      <p>
        Declare business result codes in sub-workflow details, then use <strong>Return Result</strong> to finish successfully with a code
        and optional outputs. <strong>End Workflow</strong> finishes successfully without a business result. Condition and Ping branches
        can also return or end. A Run step alias such as <code>ticket</code> exposes <code>subworkflow.ticket.status</code>,
        <code>subworkflow.ticket.resultCode</code>, and <code>subworkflow.ticket.outputs.*</code> to later steps.
      </p>
      <table className="guide-table">
        <thead><tr><th>Limit</th><th>Maximum</th></tr></thead>
        <tbody>
          <tr><td>Nested call depth</td><td>3</td></tr>
          <tr><td>Calls in one call tree</td><td>5</td></tr>
          <tr><td>Child instances in one call tree</td><td>10</td></tr>
          <tr><td>Returned outputs</td><td>32 values / 16 KB aggregate</td></tr>
        </tbody>
      </table>
      <div className="guide-callout">
        <strong>Failure and cancellation:</strong> With Continue on error off, a failed child stops the parent; with it on, the parent
        continues and should inspect the aliased status before using child outputs. Cancellation is reported only after Durable confirms
        termination of linked child runs and the parent. A pending request is not a cancelled run; refresh Execution History to check again.
      </div>
      <p className="help-text">
        Example: run a child that calls a ticketing webhook, checks its response with a Condition, and returns <code>Created</code>
        with <code>ticketId</code>. The parent can branch on <code>subworkflow.ticket.resultCode</code> and use
        <code>subworkflow.ticket.outputs.ticketId</code>. See <strong>docs/workflow-subworkflows.md</strong> for the full operator guide.
      </p>
    </div>

    <div className="guide-section">
      <h3>Example: Notify and Escalate Critical Alerts</h3>
      <div className="guide-callout guide-callout-example">
        <p><strong>Goal:</strong> When a critical alert is created, send Slack notification, wait 5 minutes, then start escalation if not acknowledged.</p>
        <table className="guide-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Step Type</th>
              <th>Configuration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>Send Notification</td>
              <td>Channel: #alerts-critical (Slack)</td>
            </tr>
            <tr>
              <td>2</td>
              <td>Wait / Delay</td>
              <td>5 minutes</td>
            </tr>
            <tr>
              <td>3</td>
              <td>Condition</td>
              <td>If alert.status equals Open, go to step 4; otherwise end</td>
            </tr>
            <tr>
              <td>4</td>
              <td>Escalate</td>
              <td>Action: Start Escalation</td>
            </tr>
          </tbody>
        </table>
        <p className="help-text" style={{ marginTop: '0.5rem' }}>
          Trigger: Alert Created, Condition: Severity = Critical.
          The workflow notifies Slack immediately, waits 5 minutes, then checks
          if the alert is still open. If so, it kicks off the escalation policy.
        </p>
      </div>
    </div>

    <div className="guide-section">
      <h3>Example: Auto-Create Jira Ticket for High-Severity Alerts</h3>
      <div className="guide-callout guide-callout-example">
        <p><strong>Goal:</strong> Automatically create a Jira ticket and assign the alert when any High or Critical alert arrives.</p>
        <table className="guide-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Step Type</th>
              <th>Configuration</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>Integration Action</td>
              <td>Action: Create Ticket, Integration: Jira Production</td>
            </tr>
            <tr>
              <td>2</td>
              <td>Update Alert</td>
              <td>Assign To: Jane (on-call lead)</td>
            </tr>
            <tr>
              <td>3</td>
              <td>Send Notification</td>
              <td>Channel: #ops-tickets (Slack)</td>
            </tr>
          </tbody>
        </table>
        <p className="help-text" style={{ marginTop: '0.5rem' }}>
          Trigger: Alert Created, Condition: Severity = High, Critical.
          Creates a Jira ticket, assigns the alert to the on-call lead, and posts a notification to the ops channel.
        </p>
      </div>
    </div>

    <div className="guide-section">
      <h3>Best Practices</h3>
      <ul className="guide-tips">
        <li><strong>Use trigger conditions to be specific</strong> -- avoid catch-all workflows that fire on every alert. Filter by severity or service.</li>
        <li><strong>Use Integration Actions for ticket payload templates</strong> -- keep request bodies, headers, and response ticket ID mapping centralized on the action definition.</li>
        <li><strong>Enable "Continue on error" on non-critical steps</strong> -- a Webhook/API timeout should not block the rest of the workflow.</li>
        <li><strong>Use Condition steps for smart branching</strong> -- check alert status before escalating to avoid redundant actions.</li>
        <li><strong>Store secrets once, reference everywhere</strong> -- use <code>{'{{secret.name}}'}</code> tokens instead of hardcoding credentials in step configs.</li>
        <li><strong>Keep workflows focused</strong> -- create separate workflows for different scenarios rather than one giant workflow.</li>
        <li><strong>Use Delay steps strategically</strong> -- give responders time to act before escalating or creating tickets.</li>
        <li><strong>Monitor execution history</strong> -- review the history panel regularly to catch failed steps or unexpected behavior.</li>
        <li><strong>Disable before editing</strong> -- temporarily disable a workflow while making major changes to avoid partial executions.</li>
      </ul>
    </div>

  </div>
);
